import {Button} from '@dropbox/dig-components/buttons';
import {Checkbox} from '@dropbox/dig-components/controls';
import {Menu} from '@dropbox/dig-components/menu';
import type {OverlayPlacement} from '@dropbox/dig-components/overlay';
import {Popover} from '@dropbox/dig-components/popover';
import {TextInput, TextInputRefObject} from '@dropbox/dig-components/text_fields';
import {Tooltip, type TooltipProps} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  CheckmarkLine,
  ChevronRightLine,
  FlagLine,
  InfoLine,
  JiraExternalLogo,
  MetricsLine,
  OpenLine,
  StarLine,
} from '@dropbox/dig-icons/assets';
import {growthbookCacheAtom} from 'atoms/growthbook';
import {workstreamTableDensityAtom} from 'atoms/workstreams';
import cx from 'classnames';
import {
  EmployeeRoleLevel,
  JiraObject,
  MetricAttachRequest,
  RedactedWorkstreamMetric,
  WorkstreamMetric,
} from 'client';
import {CancelButton} from 'components/DSYS/CancelButton';
import {usePlanningId} from 'components/DSYS/CollabProvider';
import {resolveStampedEditorStateMapValue} from 'components/DSYS/editor/dynamicNode/overlayHelpers';
import {PlanningCommentableRichText} from 'components/DSYS/editor/dynamicNode/PlanningCommentableRichText';
import {planningMapKeys} from 'components/DSYS/editor/dynamicNode/planningCommentMapKeys';
import {PlanningStructuredCommentField} from 'components/DSYS/editor/dynamicNode/PlanningStructuredCommentField';
import {
  type StructuredCommentLine,
  structuredCommentSegment,
  structuredCommentText,
} from 'components/DSYS/editor/dynamicNode/structuredCommentState';
import {EmployeeMentionTypeaheadOption} from 'components/DSYS/editor/plugins/MentionPlugin';
import type {EditorTypographyOptions} from 'components/DSYS/editor/themes/EditorTheme';
import {Link} from 'components/DSYS/Link';
import {MnpiMetricIndicator} from 'components/DSYS/MnpiMetricIndicator';
import {OptionalTooltip} from 'components/DSYS/OptionalTooltip';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {StatusButton} from 'components/DSYS/StatusButton';
import {StatusChip} from 'components/DSYS/StatusChip';
import {CURRENT_QUARTER, CURRENT_YEAR} from 'constant';
import {formatCompactMetricValue} from 'helpers/compactMetricValue';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import type {ComponentType, CSSProperties, ReactNode, SVGProps} from 'react';
import {
  Fragment,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {FadeIn, useHasMounted} from 'views/workstreams/page/FadeIn';
import {FoldToggle} from 'views/workstreams/page/FoldToggle';
import {
  getMetricLinkedWorkstreamIds,
  getNorthStarMetricOrdering,
  isClosedMetricStatus,
  isFullWorkstreamMetric,
  isPrimaryMetric,
  sortWorkstreamMetrics,
  toCalendarDate,
  toShortDate,
} from 'views/workstreams/utils';
import {buildMetricActualUpdate} from 'views/workstreams/workstreamMetricActual';

import {
  METRIC_FORECAST_PERIODS,
  MetricForecastGrid,
  MetricForecastHeader,
} from './MetricForecastGrid';
import {RedactedMetricValuePlaceholder} from './RedactedMetricValuePlaceholder';
import {SharedMetricWorkstreams} from './SharedMetricWorkstreams';
import type {DetailsTableMetric} from './workstreamDetailsTableMetric';
import {WorkstreamManageOutcomesTrigger} from './WorkstreamManageOutcomesModal';
import {
  getPlainTextTitle,
  isStarrableOutcome,
  normalizeExternalUrl,
} from './workstreamManageOutcomesUtils';
import {
  logWorkstreamMetricStatusChanged,
  type WorkstreamMetricStatusChangeSource,
} from './workstreamMetricAnalytics';
import {
  getWorkstreamMetricStatusOption,
  WorkstreamMetricStatusMenuContent,
  type WorkstreamMetricStatusOption,
  WorkstreamMetricStatusTagContent,
} from './WorkstreamMetricStatus';
import metricStatusStyles from './WorkstreamMetricStatus.module.css';
import {
  getOutcomeStatus,
  getOutcomeStatusUpdate,
  isTerminalOutcomeStatus,
  type OutcomeStatus,
  OutcomeStatusControl,
  type OutcomeStatusUpdate,
} from './WorkstreamOutcomeStatus';
import statusControlStyles from './WorkstreamStatusControl.module.css';
import styles from './WorkstreamTable.module.css';

export type WorkstreamMetricUpdate = Partial<WorkstreamMetric> & {
  touch_current_updated_at?: boolean | null;
};

export type WorkstreamMetricChangeHandler = (
  id: number,
  update: WorkstreamMetricUpdate,
  reason?: string,
  skipAuditLogOrExistingMetric?: boolean | WorkstreamMetric,
  disableSnackbar?: boolean
) => void | Promise<WorkstreamMetric | void>;

type JiraElevationActions = {
  loadElevatableJiraOutcomes?: () => Promise<JiraObject[]>;
  onElevateJiraOutcome?: (jiraObjectId: number) => Promise<void>;
  onDetachJiraOutcome?: (jiraObjectId: number) => Promise<void>;
};

type LegacyOutcomeStatusValue = 'not_done' | 'done';
export const REDESIGN_OUTCOME_FILTERS = ['milestone', 'jira'] as const;
export type RedesignOutcomeFilter = (typeof REDESIGN_OUTCOME_FILTERS)[number];

export type PlanningCollabOutcomeCommentsConfig = {
  documentId: string;
  initialContentEditorState: string | undefined;
  handleContentEditorStateChange: (
    metricId: number,
    contentEditorState: string | undefined,
    sourceText?: string
  ) => void;
  handleMetricOwnerContentEditorStateChange?: (
    metricId: number,
    contentEditorState: string | undefined,
    sourceText?: string
  ) => void;
  /**
   * Persist collab state for outcome fields keyed by map id (not the numeric title key), e.g.
   * `planningMapKeys.embedOutcome.eta`, `embedOutcome.jiraEta`, or `embedOutcome.metricSignal`.
   */
  handleOutcomeEtaEditorStateChange?: (
    mapKey: string,
    contentEditorState: string | undefined,
    sourceText?: string
  ) => void;
};

export type MetricsForecastType = 'actual' | 'budget' | 'target';
export type RedesignMetricsListVariant = 'table' | 'table-in-update-modal' | 'details';
export type RedesignOutcomesListVariant = 'table' | 'table-in-update-modal' | 'details';
export type RedesignOutcomeTooltipPlacement = NonNullable<TooltipProps['placement']>;
export type RedesignMetricTypeaheadSummaryMode = 'currentTarget' | 'baselineEoyBudget';
type RedesignMetricsListDisplayMode = 'default' | 'typeahead-node';
type RedesignOutcomesListDisplayMode = 'default' | 'typeahead-node';
type RedesignMetaTextSize = 'xsmall' | 'small';
type RedesignMetaItemKind = 'icon' | 'text';
type RedesignMetaItem = {
  key: string;
  kind: RedesignMetaItemKind;
  content: ReactNode;
};
type CompletionUrlPromptState = {
  outcomeKey: string;
  preserveActiveSection: boolean;
};
export const REDESIGN_ACTIVE_OUTCOMES_LIMIT = 3;
const REDESIGN_METRICS_LIST_VARIANT_CONFIG: Record<
  RedesignMetricsListVariant,
  {
    showPrimaryMetricIcon: boolean;
  }
> = {
  table: {
    showPrimaryMetricIcon: false,
  },
  'table-in-update-modal': {
    showPrimaryMetricIcon: false,
  },
  details: {
    showPrimaryMetricIcon: true,
  },
};

/**
 * Lets the shared metric-name cell render the safe identity fields from a redacted metric.
 * The non-identity fields are deliberately empty so this projection can never expose a value.
 */
export const getRedactedMetricIdentityProjection = (
  metric: RedactedWorkstreamMetric
): WorkstreamMetric => ({
  ...metric,
  created_at: '',
  updated_at: '',
  baseline: null,
  current: null,
  target: null,
  complete: null,
  complete_date: null,
  url: null,
  is_longterm: null,
  is_mnpi: metric.is_mnpi,
  status: null,
  metric_type: null,
  metric_forecasts: [],
  workstream_metric_links: [],
});

const REDESIGN_OUTCOME_TYPE_OPTIONS: Array<{
  value: RedesignOutcomeFilter;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  labelKey: string;
}> = [
  {
    value: 'milestone',
    icon: FlagLine,
    labelKey: 'milestones',
  },
  {
    value: 'jira',
    icon: JiraExternalLogo,
    labelKey: 'jira_initiatives_titlecase',
  },
];

const normalizeCompletionUrl = (value: string): string | null => {
  const trimmedValue = value.trim();
  if (!trimmedValue) return null;

  const normalizedValue = /^https?:\/\//i.test(trimmedValue)
    ? trimmedValue
    : `https://${trimmedValue}`;

  try {
    const url = new URL(normalizedValue);
    const isHttpUrl = url.protocol === 'http:' || url.protocol === 'https:';
    const hasValidHostname =
      url.hostname.includes('.') && !url.hostname.startsWith('.') && !url.hostname.endsWith('.');

    return isHttpUrl && hasValidHostname ? normalizedValue : null;
  } catch {
    return null;
  }
};

const renderRedesignMetaItems = (items: RedesignMetaItem[]): ReactNode[] =>
  items.flatMap((item, index) => {
    const previousItem = index > 0 ? items[index - 1] : null;
    const showDot = previousItem?.kind === 'text' && item.kind === 'text';
    const transitionNode = !previousItem ? null : showDot ? (
      <Text
        key={`${item.key}-separator`}
        size="xsmall"
        color="subtle"
        className={styles.redesignMetaDotSeparator}
        aria-hidden="true"
      >
        •
      </Text>
    ) : (
      <span key={`${item.key}-spacer`} className={styles.redesignMetaSpacer} aria-hidden="true" />
    );

    return [transitionNode, <Fragment key={item.key}>{item.content}</Fragment>];
  });

// "› Closed (N)" under a list, then (once opened) the closed items. `divided`
// draws the list's row hairline above it, so it reads as the box's last row.
const RedesignClosedItemsGroup = ({
  children,
  count,
  isExpanded,
  onToggle,
  divided = false,
}: {
  children: ReactNode;
  count: number;
  isExpanded: boolean;
  onToggle: () => void;
  divided?: boolean;
}) => {
  const mounted = useHasMounted();
  if (count === 0) {
    return null;
  }

  return (
    <Stack
      gap="4"
      className={cx(styles.redesignCompletedGroup, {
        [styles.redesignCompletedGroupDivided]: divided,
      })}
    >
      <FoldToggle
        label={t('closed').toString()}
        count={count}
        open={isExpanded}
        onToggle={onToggle}
      />
      {isExpanded && (
        // Fades in when opened after the page arrived.
        <FadeIn enabled={mounted}>
          <Box marginTop="4">{children}</Box>
        </FadeIn>
      )}
    </Stack>
  );
};

const RedesignZeroStateManageTrigger = ({
  workstreamId,
  workstreamName,
  metrics,
  jiraOutcomes = [],
  initialTab,
  isPending,
  owner,
  onSaveMetric,
  onAttachMetric,
  onDeleteMetric,
  loadElevatableJiraOutcomes,
  onElevateJiraOutcome,
  onDetachJiraOutcome,
  metricPeriodOptions,
  hiddenMnpiMetricsCount,
}: {
  workstreamId: number;
  workstreamName?: string;
  metrics: WorkstreamMetric[];
  jiraOutcomes?: JiraObject[];
  initialTab: 'metrics' | 'outcomes';
  isPending?: boolean;
  owner?: EmployeeRoleLevel | null;
  onSaveMetric: (
    metricId: number,
    update: Partial<WorkstreamMetric>,
    reason?: string,
    existingMetric?: WorkstreamMetric
  ) => Promise<WorkstreamMetric | void>;
  onAttachMetric?: (
    attachRequest: MetricAttachRequest,
    reason?: string
  ) => Promise<WorkstreamMetric | void>;
  onDeleteMetric?: (metric: WorkstreamMetric, reason: string) => Promise<void>;
  metricPeriodOptions?: string[];
  hiddenMnpiMetricsCount?: number;
} & JiraElevationActions) => (
  <div className={styles.redesignZeroStateTriggerRow}>
    <WorkstreamManageOutcomesTrigger
      workstreamId={workstreamId}
      workstreamName={workstreamName}
      metrics={metrics}
      jiraOutcomes={jiraOutcomes}
      initialTab={initialTab}
      autoAddRowOnOpen
      isPending={isPending}
      workstreamOwner={owner ?? undefined}
      onSaveMetric={onSaveMetric}
      onAttachMetric={onAttachMetric}
      onDeleteMetric={onDeleteMetric}
      loadElevatableJiraOutcomes={loadElevatableJiraOutcomes}
      onElevateJiraOutcome={onElevateJiraOutcome}
      onDetachJiraOutcome={onDetachJiraOutcome}
      metricPeriodOptions={metricPeriodOptions ?? []}
      triggerVariant="chip"
      triggerLabel={
        initialTab === 'metrics' ? t('add_metric').toString() : t('add_milestone').toString()
      }
      hiddenMnpiMetricsCount={hiddenMnpiMetricsCount}
    />
  </div>
);

const isCompletedStatus = (status: string | null | undefined) => {
  const normalizedStatus = (status ?? '').trim().toLowerCase();
  return (
    normalizedStatus === 'completed' || normalizedStatus === 'done' || normalizedStatus === 'missed'
  );
};

const isJiraObjectLike = (item: WorkstreamMetric | JiraObject): item is JiraObject => 'key' in item;

// Jira initiatives close on their status or lifecycle stage, like the backend's
// roadmap audit (helpers/roadmap_audit.py), so cancelled ones land under Closed.
const JIRA_CANCELLED_STATUSES = new Set(['cancelled', 'canceled']);
const JIRA_CLOSED_STATUSES = new Set([
  'done',
  'closed',
  'resolved',
  'complete',
  'completed',
  ...JIRA_CANCELLED_STATUSES,
]);
const normalizeJiraStatus = (value: string | null | undefined) =>
  (value ?? '').split(/\s+/).filter(Boolean).join(' ').toLowerCase();
const jiraStatusIn = (item: JiraObject, statuses: Set<string>) =>
  [item.status, item.lifecycle].some((value) => statuses.has(normalizeJiraStatus(value)));
export const isCancelledJiraOutcome = (item: JiraObject) =>
  jiraStatusIn(item, JIRA_CANCELLED_STATUSES);

const getOutcomeDate = (item: WorkstreamMetric | JiraObject): string | null =>
  item.complete_date ?? null;

const sortByCompleteDate = (a: WorkstreamMetric | JiraObject, b: WorkstreamMetric | JiraObject) => {
  const aDate = getOutcomeDate(a);
  const bDate = getOutcomeDate(b);
  const aTime = aDate ? new Date(aDate).getTime() : Infinity;
  const bTime = bDate ? new Date(bDate).getTime() : Infinity;

  return aTime - bTime;
};

const isPrimaryOutcomeForWorkstream = (item: WorkstreamMetric | JiraObject, workstreamId: number) =>
  !isJiraObjectLike(item) && isStarrableOutcome(item) && isPrimaryMetric(item, workstreamId);

export const sortOutcomesForWorkstream = (
  a: WorkstreamMetric | JiraObject,
  b: WorkstreamMetric | JiraObject,
  workstreamId: number
) => {
  const aPrimaryRank = isPrimaryOutcomeForWorkstream(a, workstreamId) ? 0 : 1;
  const bPrimaryRank = isPrimaryOutcomeForWorkstream(b, workstreamId) ? 0 : 1;

  if (aPrimaryRank !== bPrimaryRank) {
    return aPrimaryRank - bPrimaryRank;
  }

  const dateCompare = sortByCompleteDate(a, b);
  if (dateCompare !== 0) {
    return dateCompare;
  }

  return getOutcomeKey(a).localeCompare(getOutcomeKey(b));
};

export const isCompletedOutcome = (item: WorkstreamMetric | JiraObject) =>
  isJiraObjectLike(item)
    ? isCompletedStatus(item.status) || jiraStatusIn(item, JIRA_CLOSED_STATUSES)
    : item.complete === true || isCompletedStatus(item.status);

const getOutcomeKey = (item: WorkstreamMetric | JiraObject) =>
  isJiraObjectLike(item) ? `jira-${item.id}-${item.key}` : `metric-${item.id}`;

const getOutcomeStatusValue = (item: WorkstreamMetric | JiraObject) => {
  if (isJiraObjectLike(item)) return item.status ?? '';
  if (item.type === 'deliverable') {
    return item.status === 'completed' || item.status === 'missed' || item.complete
      ? 'completed'
      : 'not_started';
  }
  return '';
};

const getLegacyDeliverableStatusValue = (item: WorkstreamMetric): LegacyOutcomeStatusValue =>
  item.status === 'completed' || item.status === 'missed' || item.complete ? 'done' : 'not_done';

const getLegacyMilestoneStatusValue = (item: WorkstreamMetric): LegacyOutcomeStatusValue =>
  item.status === 'completed' || item.status === 'missed' || item.complete ? 'done' : 'not_done';

const getOutcomeStatusLabel = (item: WorkstreamMetric | JiraObject) => {
  const value = getOutcomeStatusValue(item);
  return value ? t(value).toString() : t('not_started').toString();
};

const getJiraOutcomeStatusTooltipTitle = (item: JiraObject) => {
  const tooltipParts = [item.lifecycle, item.latest_update].filter((value): value is string =>
    Boolean(value?.trim())
  );
  return tooltipParts.length > 0 ? tooltipParts.join(' • ') : getOutcomeStatusLabel(item);
};

const getJiraOutcomeButtonStatus = (status: string | null | undefined) => {
  switch ((status ?? '').trim()) {
    case 'On Track':
      return 'on_track';
    case 'At  Risk':
      return 'at_risk';
    case 'Delayed':
    case 'Deprioritized':
      return 'off_track';
    case 'Done':
    case 'Completed':
      return 'complete';
    case 'Cancelled':
    case 'Canceled':
      return 'cancelled';
    case 'To Do':
    default:
      return 'no_status';
  }
};

const ReadOnlyOutcomeStatusButton = ({
  status,
  label,
  includeArchived = false,
}: {
  status: string;
  label: string;
  includeArchived?: boolean;
}) => (
  <StatusButton
    status={status}
    includeArchived={includeArchived}
    hideText
    hideIconEnd
    hoverBackgroundOnly
    hoverChromeOnly
    disableHoverStyles
    nonInteractive
    compactIconOnly
    buttonProps={{
      'aria-label': label,
      tabIndex: -1,
    }}
  />
);

const getOutcomeIcon = (item: WorkstreamMetric | JiraObject) => {
  if (isJiraObjectLike(item)) return JiraExternalLogo;
  if (item.type === 'deliverable' || item.type === 'milestone') return FlagLine;
  return MetricsLine;
};

const getMetricSignal = (metric: WorkstreamMetric) =>
  metric.metric_type === 'leading' ? 'lead' : metric.metric_type === 'lagging' ? 'lag' : null;

export type WorkstreamMetricNameData = {
  confidentialTitleClassName?: string;
  isMnpi: boolean;
  ownerName: string;
  signal: 'lead' | 'lag' | null;
  titleText: string;
};

type WorkstreamMetricFallbackOwner = {name?: string | null};

const NorthStarMetricIcon = ({className}: {className?: string}) => {
  const label = t('north_star_metric_tooltip').toString();

  return (
    <Tooltip title={label} placement="top">
      <span
        className={cx(styles.redesignMetricPrimaryIcon, className)}
        aria-label={label}
        role="img"
      >
        <UIIcon src={StarLine} size="small" />
      </span>
    </Tooltip>
  );
};

export const getCurrentMetricsPeriod = () => `${CURRENT_YEAR}Q${CURRENT_QUARTER}`;

const parseMetricsPeriod = (period: string) => {
  const match = /^(\d{4})(?:Q([1-4])|(EOY))$/.exec(period.trim());
  if (!match) {
    return null;
  }

  const quarter = match[2] ? Number(match[2]) : null;
  const isEoy = match[3] === 'EOY';

  return {
    year: Number(match[1]),
    quarter,
    isEoy,
    sortIndex: isEoy ? 5 : (quarter ?? 0),
  };
};

export const compareMetricsPeriods = (a: string, b: string) => {
  const aParsed = parseMetricsPeriod(a);
  const bParsed = parseMetricsPeriod(b);
  if (!aParsed || !bParsed) {
    return a.localeCompare(b);
  }

  if (aParsed.year !== bParsed.year) {
    return aParsed.year - bParsed.year;
  }

  return aParsed.sortIndex - bParsed.sortIndex;
};

const getNextMetricsPeriod = (period: string) => {
  const parsed = parseMetricsPeriod(period);
  if (!parsed) {
    return period;
  }

  if (parsed.isEoy) {
    return `${parsed.year + 1}Q1`;
  }

  if (parsed.quarter === 4) {
    return `${parsed.year}EOY`;
  }

  return `${parsed.year}Q${(parsed.quarter ?? 0) + 1}`;
};

const formatMetricsPeriodQuarter = (period: string) => {
  const parsed = parseMetricsPeriod(period);
  if (!parsed) {
    return period;
  }

  return parsed.isEoy ? 'EOY' : `Q${parsed.quarter}`;
};

const formatMetricsPeriodSelectorLabel = (period: string, forecastType: MetricsForecastType) => {
  const parsed = parseMetricsPeriod(period);
  const typeLabel = t(forecastType).toString();

  if (!parsed) {
    return `${period} ${typeLabel}`;
  }

  const quarterLabel = parsed.isEoy ? 'EOY' : `Q${parsed.quarter}`;
  if (parsed.year === CURRENT_YEAR) {
    return `${quarterLabel} ${typeLabel}`;
  }

  return `${parsed.year} ${quarterLabel} ${typeLabel}`;
};

const formatMetricsPeriodColumnLabel = (period: string, forecastType: MetricsForecastType) =>
  `${formatMetricsPeriodQuarter(period)} ${t(forecastType).toString()}`;

const buildFullYearMetricsPeriods = (year: number) => [
  `${year}Q1`,
  `${year}Q2`,
  `${year}Q3`,
  `${year}Q4`,
  `${year}EOY`,
];

export const normalizeMetricsPeriodOptions = (periods: string[]) => {
  const normalizedPeriods = new Set<string>();
  const yearsWithEoy = new Set<number>();

  for (const period of periods) {
    normalizedPeriods.add(period);
    const parsed = parseMetricsPeriod(period);
    if (parsed?.isEoy) {
      yearsWithEoy.add(parsed.year);
    }
  }

  for (const year of yearsWithEoy) {
    for (const period of buildFullYearMetricsPeriods(year)) {
      normalizedPeriods.add(period);
    }
  }

  return [...normalizedPeriods].sort(compareMetricsPeriods);
};

export const buildMetricsPeriodRange = (start: string, end: string) => {
  const range: string[] = [];
  let current = start;

  while (compareMetricsPeriods(current, end) <= 0) {
    range.push(current);
    const next = getNextMetricsPeriod(current);
    if (next === current) {
      break;
    }
    current = next;
  }

  return range;
};

export const getMetricsPeriodWithOffset = (period: string, offset: number) => {
  if (offset === 0) {
    return period;
  }

  const parsed = parseMetricsPeriod(period);
  if (!parsed) {
    return period;
  }

  const quarterIndex = parsed.isEoy ? parsed.year * 4 + 4 : parsed.year * 4 + (parsed.quarter ?? 0);
  const shiftedQuarterIndex = quarterIndex + offset;
  const shiftedYear = Math.floor((shiftedQuarterIndex - 1) / 4);
  const shiftedQuarter = ((shiftedQuarterIndex - 1) % 4) + 1;

  return `${shiftedYear}Q${shiftedQuarter}`;
};

const getMetricForecastForPeriod = (metric: WorkstreamMetric, period: string) =>
  metric.metric_forecasts?.find((forecast) => forecast.period === period);
const getMetricDisplayValue = (value?: string | null) =>
  value === '' || value == null ? '-' : value;

const getMetricCurrentValue = (metric: WorkstreamMetric) => getMetricDisplayValue(metric.current);

const getMetricCurrentInputValue = (metric: WorkstreamMetric) => metric.current ?? '';

const getMetricBaselineValue = (metric: WorkstreamMetric) => getMetricDisplayValue(metric.baseline);

const getMetricBaselineInputValue = (metric: WorkstreamMetric) => metric.baseline ?? '';

type EditableMetricValueColumn = 'baseline' | 'current' | 'actual';

const getEditableMetricValueInput = (
  metric: WorkstreamMetric,
  column: EditableMetricValueColumn,
  actualPeriod?: string
) =>
  column === 'actual'
    ? (metric.metric_forecasts?.find((forecast) => forecast.period === actualPeriod)?.actual ?? '')
    : column === 'baseline'
      ? getMetricBaselineInputValue(metric)
      : getMetricCurrentInputValue(metric);

const buildMetricValueUpdateForColumn = (
  column: Exclude<EditableMetricValueColumn, 'actual'>,
  value: string
): Partial<WorkstreamMetric> => {
  const normalizedValue = value || null;
  return column === 'baseline' ? {baseline: normalizedValue} : {current: normalizedValue};
};

const getMetricSelectedValueForPeriod = (
  metric: WorkstreamMetric,
  period: string,
  forecastType: MetricsForecastType
) => {
  const forecast = getMetricForecastForPeriod(metric, period);
  if (!forecast) {
    return '-';
  }

  if (forecastType === 'actual') {
    return getMetricDisplayValue(forecast.actual);
  }

  if (forecastType === 'budget') {
    return getMetricDisplayValue(forecast.budget);
  }

  return getMetricDisplayValue(forecast.target);
};

const isEmptyMetricSummaryValue = (value: string) => value.trim() === '' || value.trim() === '-';

const getMetricSummaryDisplay = (value: string, label: string, emptyLabel: string) =>
  isEmptyMetricSummaryValue(value) ? emptyLabel : `${value} (${label})`;

const getMetricGridValueForPeriod = (
  metric: WorkstreamMetric,
  period: string,
  field: 'actual' | 'target' | 'budget'
) => {
  const forecast = getMetricForecastForPeriod(metric, period);

  if (field === 'actual') {
    return getMetricDisplayValue(forecast?.actual);
  }

  if (field === 'budget') {
    return getMetricDisplayValue(forecast?.budget);
  }

  return getMetricDisplayValue(forecast?.target);
};

const getMetricButtonStatus = (value: WorkstreamMetricStatusOption['value']) =>
  value === 'behind'
    ? 'off_track'
    : value === 'at_risk'
      ? 'at_risk'
      : value === 'no_status'
        ? 'not_started'
        : value === 'closed'
          ? 'closed'
          : 'on_track';

export const MetricStatusControl = ({
  metric,
  handleMetricChange,
  iconOnly = false,
  mediumIconOnly = false,
  onStatusChange,
  statusChangeSource,
  workstreamId,
}: {
  metric: WorkstreamMetric;
  handleMetricChange?: WorkstreamMetricChangeHandler;
  iconOnly?: boolean;
  mediumIconOnly?: boolean;
  onStatusChange?: (status: WorkstreamMetricStatusOption['value']) => void;
  statusChangeSource?: WorkstreamMetricStatusChangeSource;
  workstreamId?: number;
}) => {
  const selectedStatus = getWorkstreamMetricStatusOption(metric.status);
  const label = t(selectedStatus.labelKey).toString();
  const handleStatusSelect = (status: WorkstreamMetricStatusOption['value']) => {
    if (onStatusChange) {
      onStatusChange(status);
      return;
    }
    if (statusChangeSource && workstreamId != null && status !== selectedStatus.value) {
      logWorkstreamMetricStatusChanged(statusChangeSource, {
        workstream_id: workstreamId,
        metric_id: metric.id,
        status,
        previous_status: selectedStatus.value,
      });
    }
    handleMetricChange?.(metric.id, {status});
  };

  if (!handleMetricChange && !onStatusChange) {
    if (iconOnly) {
      return (
        <StatusButton
          status={getMetricButtonStatus(selectedStatus.value)}
          hideText
          hideIconEnd
          hoverBackgroundOnly
          hoverChromeOnly
          disableHoverStyles
          nonInteractive
          compactIconOnly={!mediumIconOnly}
          mediumIconOnly={mediumIconOnly}
          size={mediumIconOnly ? 'medium' : 'small'}
          buttonProps={{
            'aria-label': label,
            title: label,
            tabIndex: -1,
          }}
        />
      );
    }

    return (
      <span
        role="button"
        tabIndex={-1}
        aria-label={label}
        title={label}
        className={metricStatusStyles.metricStatusTag}
      >
        <WorkstreamMetricStatusTagContent
          labelKey={selectedStatus.labelKey}
          iconColor={selectedStatus.iconColor}
          iconSrc={selectedStatus.iconSrc}
        />
      </span>
    );
  }

  return (
    <Menu.Wrapper>
      {({getContentProps, getTriggerProps}) => (
        <>
          {iconOnly ? (
            <StatusButton
              status={getMetricButtonStatus(selectedStatus.value)}
              hideText
              hideIconEnd
              hoverBackgroundOnly
              hoverChromeOnly
              compactIconOnly={!mediumIconOnly}
              mediumIconOnly={mediumIconOnly}
              size={mediumIconOnly ? 'medium' : 'small'}
              buttonProps={{
                ...getTriggerProps(),
                'aria-label': label,
                title: label,
              }}
            />
          ) : (
            <button
              type="button"
              className={cx(
                metricStatusStyles.metricStatusTag,
                metricStatusStyles.metricStatusButton,
                statusControlStyles.interactiveStatusTrigger
              )}
              {...getTriggerProps()}
              aria-label={label}
              title={label}
            >
              <WorkstreamMetricStatusTagContent
                labelKey={selectedStatus.labelKey}
                iconColor={selectedStatus.iconColor}
                iconSrc={selectedStatus.iconSrc}
              />
            </button>
          )}
          <WorkstreamMetricStatusMenuContent
            selectedStatus={selectedStatus}
            getContentProps={getContentProps}
            onSelect={handleStatusSelect}
          />
        </>
      )}
    </Menu.Wrapper>
  );
};

export const EditableMetricValue = ({
  className,
  metric,
  metricTitleText,
  handleMetricChange,
  column,
  actualPeriod,
  isPending = false,
  showTouchCurrentUpdatedAtControl = false,
  touchCurrentUpdatedAtChecked = false,
}: {
  className?: string;
  metric: WorkstreamMetric;
  metricTitleText: string;
  column: EditableMetricValueColumn;
  actualPeriod?: string;
  handleMetricChange?: WorkstreamMetricChangeHandler;
  isPending?: boolean;
  showTouchCurrentUpdatedAtControl?: boolean;
  touchCurrentUpdatedAtChecked?: boolean;
}) => {
  const label = t(column).toString();
  const externalValue = useMemo(
    () => getEditableMetricValueInput(metric, column, actualPeriod),
    [actualPeriod, column, metric]
  );
  const shouldShowTouchCurrentUpdatedAtControl =
    column === 'current' && showTouchCurrentUpdatedAtControl;
  const valueText = externalValue || '-';
  const compactValue = useMemo(() => formatCompactMetricValue(valueText), [valueText]);
  const [draftValue, setDraftValue] = useState(externalValue);
  const [isEditing, setIsEditing] = useState(false);
  const cancelCommitRef = useRef(false);

  useEffect(() => {
    if (!isEditing) {
      setDraftValue(externalValue);
    }
  }, [externalValue, isEditing]);

  const commitValue = useCallback(async () => {
    if (cancelCommitRef.current || !handleMetricChange || draftValue === externalValue) {
      setIsEditing(false);
      return;
    }

    try {
      await handleMetricChange(metric.id, {
        ...(column === 'actual' && actualPeriod
          ? buildMetricActualUpdate(metric, actualPeriod, draftValue)
          : column !== 'actual'
            ? buildMetricValueUpdateForColumn(column, draftValue)
            : {}),
        ...(shouldShowTouchCurrentUpdatedAtControl ? {touch_current_updated_at: false} : {}),
      });
    } catch {
      setDraftValue(externalValue);
    } finally {
      setIsEditing(false);
    }
  }, [
    actualPeriod,
    column,
    draftValue,
    externalValue,
    handleMetricChange,
    metric,
    shouldShowTouchCurrentUpdatedAtControl,
  ]);

  const noChangeControl = shouldShowTouchCurrentUpdatedAtControl ? (
    <label className={styles.redesignMetricsCurrentNoChangeLabel}>
      <Checkbox
        checked={touchCurrentUpdatedAtChecked}
        disabled={isPending || !handleMetricChange}
        onChange={(event) => {
          const checked = event.currentTarget.checked;
          void handleMetricChange?.(metric.id, {
            ...(checked ? buildMetricValueUpdateForColumn(column, externalValue) : {}),
            touch_current_updated_at: checked,
          });
        }}
        aria-label={`${t('workstream_update_metric_no_change').toString()} ${
          metricTitleText || metric.id
        }`}
      />
      <Text size="small" color="subtle" className={styles.redesignMetricsCurrentNoChangeText}>
        {t('workstream_update_metric_no_change').toString()}
      </Text>
    </label>
  ) : null;

  if (!isEditing) {
    const displayButton = (
      <button
        type="button"
        disabled={isPending}
        className={cx(styles.redesignMetricsCurrentDisplayButton, className)}
        onClick={() => {
          cancelCommitRef.current = false;
          setIsEditing(true);
        }}
        aria-label={`${t('edit').toString()} ${label.toLowerCase()} ${
          metricTitleText || metric.id
        }`}
      >
        <OptionalTooltip
          enabled={Boolean(compactValue.tooltipValue)}
          title={<Text size="small">{compactValue.tooltipValue}</Text>}
          placement="top"
          wrapperClassName={cx(
            styles.redesignMetricsValueTooltipTrigger,
            styles.redesignMetricsCurrentValue,
            className
          )}
        >
          <span
            className={cx(
              styles.redesignMetricsValueText,
              styles.redesignMetricsCurrentValue,
              className,
              compactValue.tooltipValue && styles.redesignMetricsValueWithTooltip
            )}
          >
            {compactValue.displayValue}
          </span>
        </OptionalTooltip>
      </button>
    );

    return noChangeControl ? (
      <div className={styles.redesignMetricsCurrentUpdateControl}>
        {displayButton}
        {noChangeControl}
      </div>
    ) : (
      displayButton
    );
  }

  const input = (
    <TextInput
      autoFocus
      size="small"
      isTransparent
      disabled={isPending}
      className={cx(styles.redesignMetricsCurrentInput, className)}
      value={draftValue}
      onBlur={() => void commitValue()}
      onChange={(event) => setDraftValue(event.currentTarget.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.currentTarget.blur();
          return;
        }

        if (event.key === 'Escape') {
          event.preventDefault();
          cancelCommitRef.current = true;
          setDraftValue(externalValue);
          setIsEditing(false);
          event.currentTarget.blur();
        }
      }}
      placeholder="-"
      aria-label={`${label} ${metricTitleText || metric.id}`}
    />
  );

  return noChangeControl ? (
    <div className={styles.redesignMetricsCurrentUpdateControl}>
      {input}
      {noChangeControl}
    </div>
  ) : (
    input
  );
};

export const getOutcomeMetaParts = (
  item: WorkstreamMetric | JiraObject,
  fallbackOwner?: {name?: string | null}
) => {
  const ownerName = item.owner?.name ?? fallbackOwner?.name ?? '';
  const dueDate = getOutcomeDate(item);
  const dueDateObject = dueDate ? toCalendarDate(dueDate) : null;
  const isOverdue =
    dueDateObject !== null &&
    !Number.isNaN(dueDateObject.getTime()) &&
    dueDateObject.getTime() < Date.now();
  return {
    dueLabel: dueDate ? toShortDate(dueDate) : t('no_eta').toString(),
    isOverdue,
    ownerName,
    jiraKey: isJiraObjectLike(item) ? item.key : undefined,
  };
};

export const shouldHighlightOutcomeEta = (isOverdue: boolean, isClosed: boolean) =>
  isOverdue && !isClosed;

export const getRedesignOutcomeFilter = (
  item: WorkstreamMetric | JiraObject
): RedesignOutcomeFilter | null => {
  if (isJiraObjectLike(item)) return 'jira';
  if (item.type === 'milestone' || item.type === 'deliverable') return 'milestone';
  return null;
};

export const isRedesignOutcomeIncludedByFilters = (
  item: WorkstreamMetric | JiraObject,
  selectedFilters: RedesignOutcomeFilter[]
) => {
  const filter = getRedesignOutcomeFilter(item);
  if (!filter) return false;
  return selectedFilters.includes(filter);
};

const getRedesignOutcomeTypeLabel = (item: WorkstreamMetric | JiraObject) => {
  const filter = getRedesignOutcomeFilter(item);
  if (filter === 'milestone') return t('milestone').toString();
  if (filter === 'jira') return t('jira_initiative').toString();
  return '';
};

export const RedesignMetricsPeriodSelector = ({
  periods,
  selectedPeriod,
  selectedForecastType,
  onSelect,
  onForecastTypeSelect,
}: {
  periods: string[];
  selectedPeriod: string;
  selectedForecastType: MetricsForecastType;
  onSelect: (period: string) => void;
  onForecastTypeSelect: (forecastType: MetricsForecastType) => void;
}) => {
  const groupedPeriods = useMemo(() => {
    const groups = new Map<string, string[]>();

    periods.forEach((period) => {
      const year = period.slice(0, 4);
      const existingPeriods = groups.get(year) ?? [];
      existingPeriods.push(period);
      groups.set(year, existingPeriods);
    });

    return Array.from(groups.entries())
      .sort(([leftYear], [rightYear]) => Number(leftYear) - Number(rightYear))
      .map(([year, yearPeriods]) => ({
        year,
        periods: yearPeriods.sort(compareMetricsPeriods),
      }));
  }, [periods]);
  const currentAndFutureYears = groupedPeriods.filter(({year}) => Number(year) >= CURRENT_YEAR);
  const previousYears = groupedPeriods.filter(({year}) => Number(year) < CURRENT_YEAR);
  const selectedLabel = formatMetricsPeriodSelectorLabel(selectedPeriod, selectedForecastType);

  return (
    <Menu.Wrapper slideSubmenu style={{marginRight: -8, marginLeft: 'auto'}}>
      {({getContentProps, getTriggerProps}) => (
        <>
          <Button
            variant="borderless"
            size="small"
            withDropdownIcon
            className={styles.redesignHeaderSelectorButton}
            {...getTriggerProps()}
          >
            {selectedLabel}
          </Button>
          <Menu.Content {...getContentProps()} placement="bottom-end" style={{minWidth: 120}}>
            {currentAndFutureYears.map(({year, periods: yearPeriods}) => (
              <Menu.Segment key={year} withLabel={year}>
                {yearPeriods.map((period) => (
                  <Menu.ActionItem
                    key={period}
                    onClick={() => onSelect(period)}
                    withRightAccessory={
                      selectedPeriod === period ? <UIIcon src={CheckmarkLine} /> : undefined
                    }
                  >
                    {formatMetricsPeriodQuarter(period)}
                  </Menu.ActionItem>
                ))}
              </Menu.Segment>
            ))}
            {previousYears.length > 0 ? (
              <Menu.Segment>
                <Menu.Submenu
                  withTriggerContent={t('previous_years').toString()}
                  withRightAccessory={
                    <UIIcon src={ChevronRightLine} className={atoms({color: 'Text Subtle'})} />
                  }
                >
                  <Menu.Segment>
                    {previousYears.map(({year, periods: yearPeriods}) => (
                      <Menu.Submenu
                        key={year}
                        withTriggerContent={year}
                        withRightAccessory={
                          <UIIcon
                            src={ChevronRightLine}
                            className={atoms({color: 'Text Subtle'})}
                          />
                        }
                      >
                        <Menu.Segment withLabel={year}>
                          {yearPeriods.map((period) => (
                            <Menu.ActionItem
                              key={period}
                              onClick={() => onSelect(period)}
                              withRightAccessory={
                                selectedPeriod === period ? (
                                  <UIIcon src={CheckmarkLine} />
                                ) : undefined
                              }
                            >
                              {formatMetricsPeriodQuarter(period)}
                            </Menu.ActionItem>
                          ))}
                        </Menu.Segment>
                      </Menu.Submenu>
                    ))}
                  </Menu.Segment>
                </Menu.Submenu>
              </Menu.Segment>
            ) : null}
            <Menu.Segment withLabel={t('type').toString()}>
              <Menu.ActionItem
                onClick={() => onForecastTypeSelect('actual')}
                withRightAccessory={
                  selectedForecastType === 'actual' ? <UIIcon src={CheckmarkLine} /> : undefined
                }
              >
                {t('actual').toString()}
              </Menu.ActionItem>
              <Menu.ActionItem
                onClick={() => onForecastTypeSelect('budget')}
                withRightAccessory={
                  selectedForecastType === 'budget' ? <UIIcon src={CheckmarkLine} /> : undefined
                }
              >
                {t('budget').toString()}
              </Menu.ActionItem>
              <Menu.ActionItem
                onClick={() => onForecastTypeSelect('target')}
                withRightAccessory={
                  selectedForecastType === 'target' ? <UIIcon src={CheckmarkLine} /> : undefined
                }
              >
                {t('target')}
              </Menu.ActionItem>
            </Menu.Segment>
          </Menu.Content>
        </>
      )}
    </Menu.Wrapper>
  );
};

const DEFAULT_COMMENTABLE_TITLE_TYPOGRAPHY: EditorTypographyOptions = {
  textColor: 'inherit',
  textSize: 'small',
};

type StructuredCommentSegmentPart = {
  id: string;
  text?: string;
  format?: number;
  style?: string;
};

const REDESIGN_OVERDUE_META_TEXT_STYLE = 'color: var(--dig-color__alert__base);';

const getConfidentialMetricTitleClassName = (isConfidential: boolean) =>
  isConfidential ? styles.redesignMetricConfidentialTitle : undefined;

export const getWorkstreamMetricNameData = (
  metric: WorkstreamMetric,
  owner?: WorkstreamMetricFallbackOwner
): WorkstreamMetricNameData => {
  const isMnpi = Boolean(metric.is_mnpi);

  return {
    confidentialTitleClassName: getConfidentialMetricTitleClassName(isMnpi),
    isMnpi,
    ownerName: metric.owner?.name ?? owner?.name ?? '',
    signal: getMetricSignal(metric),
    titleText: getPlainTextTitle(metric.title),
  };
};

const buildStructuredCommentSummaryLine = (
  segments: StructuredCommentSegmentPart[]
): StructuredCommentLine =>
  segments.reduce<StructuredCommentLine>((tokens, segment) => {
    if (!segment.text) {
      return tokens;
    }

    if (tokens.length > 0) {
      tokens.push(structuredCommentText(' • '));
    }

    tokens.push(structuredCommentSegment(segment.id, segment.text, segment.format, segment.style));
    return tokens;
  }, []);

const RedesignPlanningCommentableMetricTitleMeta = ({
  metric,
  metricTitle,
  metaDisplay,
  metaSegments,
  metaAccessory,
  metricTitleClassName,
  planningCollabOutcomeComments,
  commentMentions = [],
}: {
  metric: WorkstreamMetric;
  metricTitle: string;
  metaDisplay: string;
  metaSegments: StructuredCommentSegmentPart[];
  metaAccessory?: ReactNode;
  metricTitleClassName?: string;
  planningCollabOutcomeComments?: PlanningCollabOutcomeCommentsConfig;
  commentMentions?: EmployeeMentionTypeaheadOption[];
}) => {
  const planningId = usePlanningId();
  const persistHandler = planningCollabOutcomeComments?.handleOutcomeEtaEditorStateChange;
  const mapKey = planningMapKeys.embedOutcome.metricTitleAndMeta(metric.id);
  const metaLine = buildStructuredCommentSummaryLine(metaSegments);
  const lines = [
    [structuredCommentSegment('title', metricTitle)],
    ...(metaLine.length > 0 ? [metaLine] : []),
  ];
  const canUse =
    Boolean(planningCollabOutcomeComments) &&
    Boolean(persistHandler) &&
    typeof metric.id === 'number' &&
    metric.id > 0;
  const accessoryWrapperRef = useRef<HTMLDivElement>(null);
  const metaLineElementRef = useRef<HTMLElement | null>(null);
  const metaLineIndex = lines.length - 1;
  const [metaAccessoryTop, setMetaAccessoryTop] = useState<number | null>(null);
  const [metaAccessoryWidth, setMetaAccessoryWidth] = useState<number | null>(null);
  const updateMetaAccessoryPosition = useCallback(() => {
    const wrapper = accessoryWrapperRef.current;
    const content = wrapper?.querySelector(`.${styles.redesignMetricTitleAndMetaContent}`);
    const metaLineElement = content?.children.item(metaLineIndex) as HTMLElement | null;
    const accessoryElement = wrapper?.querySelector(
      `.${styles.redesignMetricTitleMetaLineAccessory}`
    ) as HTMLElement | null;

    if (metaLineElementRef.current && metaLineElementRef.current !== metaLineElement) {
      metaLineElementRef.current.classList.remove(styles.redesignMetricTitleMetaLineWithAccessory);
    }
    metaLineElementRef.current = metaLineElement;

    if (!wrapper || !metaLineElement) {
      setMetaAccessoryTop(null);
      return;
    }

    metaLineElement.classList.add(styles.redesignMetricTitleMetaLineWithAccessory);

    const wrapperRect = wrapper.getBoundingClientRect();
    const metaLineRect = metaLineElement.getBoundingClientRect();
    const accessoryRect = accessoryElement?.getBoundingClientRect();
    setMetaAccessoryWidth(accessoryRect ? Math.ceil(accessoryRect.width) : 16);
    setMetaAccessoryTop(metaLineRect.top - wrapperRect.top + (metaLineRect.height - 16) / 2);
  }, [metaLineIndex]);

  useLayoutEffect(() => {
    if (!metaAccessory) {
      return;
    }

    updateMetaAccessoryPosition();
    const frameId = window.requestAnimationFrame(updateMetaAccessoryPosition);
    const wrapper = accessoryWrapperRef.current;
    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => updateMetaAccessoryPosition());

    if (wrapper) {
      resizeObserver?.observe(wrapper);
    }
    window.addEventListener('resize', updateMetaAccessoryPosition);

    return () => {
      window.cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateMetaAccessoryPosition);
      metaLineElementRef.current?.classList.remove(styles.redesignMetricTitleMetaLineWithAccessory);
    };
  }, [metaAccessory, updateMetaAccessoryPosition]);

  if (!canUse || !planningCollabOutcomeComments || !persistHandler) {
    return (
      <Box display="flex" flexDirection="column">
        <Text size="small" className={metricTitleClassName}>
          {metricTitle}
        </Text>
        {metaDisplay || metaAccessory ? (
          <Box className={styles.redesignMetricMetaRow}>
            {metaAccessory}
            {metaAccessory && metaDisplay ? (
              <span className={styles.redesignMetaSpacer} aria-hidden="true" />
            ) : null}
            {metaDisplay ? (
              <Text size="xsmall" color="subtle">
                {metaDisplay}
              </Text>
            ) : null}
          </Box>
        ) : null}
      </Box>
    );
  }

  const mapJson = planningCollabOutcomeComments.initialContentEditorState;

  const field = (
    <PlanningStructuredCommentField
      key={`${metric.id}-metricTitleAndMeta`}
      planningId={planningId ?? undefined}
      mapKey={mapKey}
      mapJson={mapJson}
      lines={lines}
      editable={false}
      hideToolbar
      disableBorder
      minHeight={0}
      editablePaddingX="0"
      editablePaddingY="0"
      squareBorders
      textColor="inherit"
      textSize="small"
      contentClassName={cx(
        styles.redesignMetricsTypeaheadTitleContent,
        styles.redesignMetricTitleAndMetaContent,
        metricTitleClassName && styles.redesignMetricConfidentialTitleAndMetaContent
      )}
      selectionControls={['styling', 'highlighting', 'comments']}
      commentMentions={commentMentions}
      onPersistKey={persistHandler}
    />
  );

  if (!metaAccessory) {
    return field;
  }

  return (
    <Box
      ref={accessoryWrapperRef}
      className={styles.redesignMetricTitleMetaWithAccessory}
      style={
        {
          '--redesign-metric-title-meta-accessory-width': `${metaAccessoryWidth ?? 16}px`,
        } as CSSProperties
      }
    >
      <span
        className={styles.redesignMetricTitleMetaLineAccessory}
        style={metaAccessoryTop == null ? {visibility: 'hidden'} : {top: metaAccessoryTop}}
      >
        {metaAccessory}
      </span>
      {field}
    </Box>
  );
};

export const RedesignCommentableTitle = ({
  item,
  commentMentions = [],
  truncateLines,
  planningCollabOutcomeComments,
  titleWrapperClassName,
  titleTypography,
  titleContentClassName,
  onTitleTruncateOverflowChange,
}: {
  item: WorkstreamMetric | JiraObject;
  commentMentions?: EmployeeMentionTypeaheadOption[];
  truncateLines?: number;
  planningCollabOutcomeComments?: PlanningCollabOutcomeCommentsConfig;
  titleWrapperClassName?: string;
  titleTypography?: EditorTypographyOptions;
  titleContentClassName?: string;
  onTitleTruncateOverflowChange?: (isTruncated: boolean) => void;
}) => {
  const planningMetric = isJiraObjectLike(item) ? null : (item as WorkstreamMetric);
  const isPlanningCollabTitle = Boolean(planningCollabOutcomeComments) && planningMetric != null;

  const resolvedTitleTypography = useMemo(
    () => ({
      ...DEFAULT_COMMENTABLE_TITLE_TYPOGRAPHY,
      ...titleTypography,
    }),
    [titleTypography]
  );
  const resolvedTitleWrapperClassName = cx(
    styles.redesignInlineEditableContent,
    titleWrapperClassName
  );

  if (isPlanningCollabTitle && planningCollabOutcomeComments && planningMetric) {
    const mapJson = planningCollabOutcomeComments.initialContentEditorState;
    const titleMapKey = String(planningMetric.id);
    const planningTitleValue = resolveStampedEditorStateMapValue(
      mapJson,
      titleMapKey,
      planningMetric.title
    );
    return (
      <Box className={resolvedTitleWrapperClassName}>
        <Suspense fallback={planningMetric.title}>
          <PlanningCommentableRichText
            key={`${planningMetric.id}-${planningMetric.title}`}
            value={planningTitleValue}
            editable={false}
            hideToolbar
            disableBorder
            minHeight={0}
            editablePaddingX="0"
            editablePaddingY="0"
            squareBorders
            {...resolvedTitleTypography}
            contentClassName={titleContentClassName}
            truncateLines={truncateLines}
            onTruncateOverflowChange={onTitleTruncateOverflowChange}
            commentMentions={commentMentions}
            onChange={(state) => {
              planningCollabOutcomeComments.handleContentEditorStateChange(
                planningMetric.id,
                state.toString(),
                planningMetric.title
              );
            }}
          />
        </Suspense>
      </Box>
    );
  }

  return (
    <Box className={resolvedTitleWrapperClassName}>
      <RichTextArea
        key={item.title}
        value={item.title}
        squareBorders
        {...resolvedTitleTypography}
        contentClassName={titleContentClassName}
        truncateLines={truncateLines}
        onTruncateOverflowChange={onTitleTruncateOverflowChange}
      />
    </Box>
  );
};

const RedesignPlanningCommentableMetricOwner = ({
  metric,
  ownerDisplayName,
  planningCollabOutcomeComments,
  commentMentions = [],
  contentClassName,
  fallbackTextSize = 'xsmall',
}: {
  metric: WorkstreamMetric;
  ownerDisplayName: string;
  planningCollabOutcomeComments?: PlanningCollabOutcomeCommentsConfig;
  commentMentions?: EmployeeMentionTypeaheadOption[];
  contentClassName?: string;
  fallbackTextSize?: RedesignMetaTextSize;
}) => {
  const ownerHandler = planningCollabOutcomeComments?.handleMetricOwnerContentEditorStateChange;
  const canUsePlanningCollabOwner =
    Boolean(planningCollabOutcomeComments) &&
    Boolean(ownerHandler) &&
    typeof metric.id === 'number' &&
    metric.id > 0 &&
    ownerDisplayName.trim().length > 0;

  if (!canUsePlanningCollabOwner || !planningCollabOutcomeComments || !ownerHandler) {
    return (
      <Text size={fallbackTextSize} color="subtle" className={contentClassName}>
        {ownerDisplayName}
      </Text>
    );
  }

  const mapJson = planningCollabOutcomeComments.initialContentEditorState;
  const ownerMapKey = planningMapKeys.embedOutcome.owner(metric.id);
  const planningOwnerValue = resolveStampedEditorStateMapValue(
    mapJson,
    ownerMapKey,
    ownerDisplayName
  );

  return (
    <Suspense fallback={ownerDisplayName}>
      <PlanningCommentableRichText
        key={`${metric.id}-owner-${ownerDisplayName}`}
        value={planningOwnerValue}
        editable={false}
        hideToolbar
        disableBorder
        minHeight={0}
        editablePaddingX="0"
        editablePaddingY="0"
        squareBorders
        textColor="subtle"
        textSize="small"
        contentClassName={contentClassName}
        commentMentions={commentMentions}
        onChange={(state) => {
          ownerHandler(metric.id, state.toString(), ownerDisplayName);
        }}
      />
    </Suspense>
  );
};

const RedesignPlanningCommentableMetricDetails = ({
  metric,
  sectionLabel,
  metricTitle,
  summaryDisplay,
  summarySegments,
  showSectionLabel = true,
  metricTitleClassName,
  planningCollabOutcomeComments,
  commentMentions = [],
}: {
  metric: WorkstreamMetric;
  sectionLabel: string;
  metricTitle: string;
  summaryDisplay: string;
  summarySegments: StructuredCommentSegmentPart[];
  showSectionLabel?: boolean;
  metricTitleClassName?: string;
  planningCollabOutcomeComments?: PlanningCollabOutcomeCommentsConfig;
  commentMentions?: EmployeeMentionTypeaheadOption[];
}) => {
  const planningId = usePlanningId();
  const persistHandler = planningCollabOutcomeComments?.handleOutcomeEtaEditorStateChange;
  const mapKey = planningMapKeys.embedOutcome.metricDetails(metric.id);
  const summaryLine = buildStructuredCommentSummaryLine(summarySegments);
  const lines = [
    ...(showSectionLabel ? [[structuredCommentSegment('sectionLabel', sectionLabel, 1)]] : []),
    [structuredCommentSegment('title', metricTitle)],
    ...(summaryLine.length > 0 ? [summaryLine] : []),
  ];
  const canUsePlanningCollabDetails =
    Boolean(planningCollabOutcomeComments) &&
    Boolean(persistHandler) &&
    typeof metric.id === 'number' &&
    metric.id > 0;

  if (!canUsePlanningCollabDetails || !planningCollabOutcomeComments || !persistHandler) {
    return (
      <Box display="flex" flexDirection="column">
        {showSectionLabel ? (
          <Text isBold size="medium">
            {sectionLabel}
          </Text>
        ) : null}
        <Text
          size="medium"
          className={cx(styles.redesignMetricsTypeaheadDetailsTitle, metricTitleClassName)}
        >
          {metricTitle}
        </Text>
        {summaryDisplay ? (
          <Text size="small" color="subtle">
            {summaryDisplay}
          </Text>
        ) : null}
      </Box>
    );
  }

  const mapJson = planningCollabOutcomeComments.initialContentEditorState;

  return (
    <Box>
      <PlanningStructuredCommentField
        key={`${metric.id}-metricDetails`}
        planningId={planningId ?? undefined}
        mapKey={mapKey}
        mapJson={mapJson}
        lines={lines}
        editable={false}
        hideToolbar
        disableBorder
        minHeight={0}
        editablePaddingX="0"
        editablePaddingY="0"
        squareBorders
        textColor="inherit"
        textSize="medium"
        contentClassName={cx(
          styles.redesignMetricsTypeaheadTitleContent,
          styles.redesignMetricsTypeaheadDetailsContent,
          !showSectionLabel && styles.redesignMetricsTypeaheadDetailsContentWithoutSection,
          metricTitleClassName && styles.redesignMetricsTypeaheadConfidentialDetailsContent
        )}
        selectionControls={['styling', 'highlighting', 'comments']}
        commentMentions={commentMentions}
        onPersistKey={persistHandler}
      />
    </Box>
  );
};

const RedesignPlanningCommentableMetricSignal = ({
  metric,
  signal,
  planningCollabOutcomeComments,
  commentMentions = [],
  contentClassName,
}: {
  metric: WorkstreamMetric;
  signal: 'lead' | 'lag';
  planningCollabOutcomeComments?: PlanningCollabOutcomeCommentsConfig;
  commentMentions?: EmployeeMentionTypeaheadOption[];
  contentClassName?: string;
}) => {
  const persistHandler = planningCollabOutcomeComments?.handleOutcomeEtaEditorStateChange;
  const mapKey = planningMapKeys.embedOutcome.metricSignal(metric.id);
  const canUsePlanningCollabSignal =
    Boolean(planningCollabOutcomeComments) &&
    Boolean(persistHandler) &&
    typeof metric.id === 'number' &&
    metric.id > 0;

  const fallbackLabel = t(signal).toString();

  if (!canUsePlanningCollabSignal || !planningCollabOutcomeComments || !persistHandler) {
    return (
      <Text
        size="xsmall"
        color="subtle"
        className={cx(styles.redesignMetricSignalText, contentClassName)}
      >
        {fallbackLabel}
      </Text>
    );
  }

  const mapJson = planningCollabOutcomeComments.initialContentEditorState;
  const planningSignalValue = resolveStampedEditorStateMapValue(mapJson, mapKey, fallbackLabel);

  return (
    <Suspense fallback={fallbackLabel}>
      <PlanningCommentableRichText
        key={`${metric.id}-metricSignal-${signal}`}
        value={planningSignalValue}
        editable={false}
        hideToolbar
        disableBorder
        minHeight={0}
        editablePaddingX="0"
        editablePaddingY="0"
        squareBorders
        textColor="subtle"
        textSize="small"
        contentClassName={cx(styles.redesignMetricSignalText, contentClassName)}
        commentMentions={commentMentions}
        onChange={(state) => {
          persistHandler(mapKey, state.toString(), fallbackLabel);
        }}
      />
    </Suspense>
  );
};

const RedesignPlanningCommentableMetricMetaRow = ({
  metric,
  signalLabel,
  ownerDisplayName,
  planningCollabOutcomeComments,
  commentMentions = [],
}: {
  metric: WorkstreamMetric;
  signalLabel?: string;
  ownerDisplayName?: string;
  planningCollabOutcomeComments?: PlanningCollabOutcomeCommentsConfig;
  commentMentions?: EmployeeMentionTypeaheadOption[];
}) => {
  const planningId = usePlanningId();
  const persistHandler = planningCollabOutcomeComments?.handleOutcomeEtaEditorStateChange;
  const mapKey = planningMapKeys.embedOutcome.metricMeta(metric.id);
  const lines = [
    buildStructuredCommentSummaryLine([
      {id: 'signal', text: signalLabel},
      {id: 'owner', text: ownerDisplayName},
    ]),
  ].filter((line) => line.length > 0);
  const canUse =
    Boolean(planningCollabOutcomeComments) &&
    Boolean(persistHandler) &&
    typeof metric.id === 'number' &&
    metric.id > 0 &&
    lines.length > 0;

  if (!canUse || !planningCollabOutcomeComments || !persistHandler) {
    return null;
  }

  return (
    <PlanningStructuredCommentField
      key={`${metric.id}-metricMeta`}
      planningId={planningId ?? undefined}
      mapKey={mapKey}
      mapJson={planningCollabOutcomeComments.initialContentEditorState}
      lines={lines}
      editable={false}
      hideToolbar
      disableBorder
      minHeight={0}
      editablePaddingX="0"
      editablePaddingY="0"
      squareBorders
      textColor="subtle"
      textSize="small"
      contentClassName={styles.redesignMetaRowStructuredContent}
      selectionControls={['comments']}
      commentMentions={commentMentions}
      onPersistKey={persistHandler}
    />
  );
};

export const WorkstreamMetricNameCellContent = ({
  commentMentions,
  metric,
  owner,
  redactedMetric = false,
  nameData = getWorkstreamMetricNameData(metric, owner),
  planningCollabOutcomeComments,
  showPrimaryMetricIcon = false,
  workstreamId,
}: {
  commentMentions?: EmployeeMentionTypeaheadOption[];
  metric: WorkstreamMetric;
  nameData?: WorkstreamMetricNameData;
  owner?: WorkstreamMetricFallbackOwner;
  planningCollabOutcomeComments?: PlanningCollabOutcomeCommentsConfig;
  redactedMetric?: boolean;
  showPrimaryMetricIcon?: boolean;
  workstreamId?: number;
}) => {
  const {isGtmPilotChangesEnabled} = useAtomValue(growthbookCacheAtom);
  const {confidentialTitleClassName, isMnpi, ownerName, signal} = nameData;
  const metricMetaItems: RedesignMetaItem[] = [];
  const linkedWorkstreamIds = getMetricLinkedWorkstreamIds(metric);

  if (isMnpi) {
    metricMetaItems.push({
      key: 'mnpi',
      kind: 'icon',
      content: (
        <MnpiMetricIndicator
          className={styles.redesignMetricMnpiIcon}
          tooltip={redactedMetric ? t('redacted_metric_tooltip').toString() : undefined}
        />
      ),
    });
  }

  if (showPrimaryMetricIcon && workstreamId != null && isPrimaryMetric(metric, workstreamId)) {
    metricMetaItems.push({
      key: 'primary',
      kind: 'icon',
      content: <NorthStarMetricIcon />,
    });
  }

  if (planningCollabOutcomeComments && (signal || ownerName)) {
    metricMetaItems.push({
      key: 'meta',
      kind: 'text',
      content: (
        <RedesignPlanningCommentableMetricMetaRow
          metric={metric}
          signalLabel={signal ? t(signal).toString() : undefined}
          ownerDisplayName={ownerName}
          planningCollabOutcomeComments={planningCollabOutcomeComments}
          commentMentions={commentMentions}
        />
      ),
    });
  } else {
    if (signal) {
      metricMetaItems.push({
        key: 'signal',
        kind: 'text',
        content: (
          <RedesignPlanningCommentableMetricSignal
            metric={metric}
            signal={signal}
            planningCollabOutcomeComments={planningCollabOutcomeComments}
            commentMentions={commentMentions}
          />
        ),
      });
    }

    if (ownerName) {
      metricMetaItems.push({
        key: 'owner',
        kind: 'text',
        content: (
          <RedesignPlanningCommentableMetricOwner
            metric={metric}
            ownerDisplayName={ownerName}
            planningCollabOutcomeComments={planningCollabOutcomeComments}
            commentMentions={commentMentions}
            contentClassName={styles.redesignMetricOwnerMeta}
          />
        ),
      });
    }
  }

  if (linkedWorkstreamIds.length > 1) {
    metricMetaItems.push({
      key: 'shared-workstreams',
      kind: 'text',
      content: (
        <SharedMetricWorkstreams
          workstreamIds={linkedWorkstreamIds}
          currentWorkstreamId={workstreamId}
        />
      ),
    });
  }

  return (
    <Stack gap="2" className={styles.redesignMetricContent}>
      <Box className={styles.redesignMetricTitleRow}>
        <Box className={styles.redesignMetricTitle}>
          <OptionalTooltip
            enabled={isGtmPilotChangesEnabled && Boolean(metric.definition?.trim())}
            title={<Text size="small">{metric.definition}</Text>}
            placement="right"
            wrapperClassName={styles.redesignMetricDefinitionTooltipTrigger}
          >
            <span
              className={
                isGtmPilotChangesEnabled
                  ? styles.redesignMetricDefinitionTooltipContent
                  : styles.redesignMetricTitleTooltipTrigger
              }
            >
              <RedesignCommentableTitle
                item={metric}
                commentMentions={commentMentions}
                planningCollabOutcomeComments={planningCollabOutcomeComments}
                titleContentClassName={confidentialTitleClassName}
              />
              {isGtmPilotChangesEnabled && metric.url?.trim() ? (
                <a
                  href={normalizeExternalUrl(metric.url.trim())}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={t('open_metric_link_for', {
                    name: getPlainTextTitle(metric.title) || metric.id,
                  }).toString()}
                  className={styles.redesignMetricExternalLink}
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={(event) => event.stopPropagation()}
                >
                  <UIIcon src={OpenLine} size="small" />
                </a>
              ) : null}
            </span>
          </OptionalTooltip>
        </Box>
      </Box>
      {metricMetaItems.length > 0 ? (
        <Box className={styles.redesignMetricMetaRow}>
          {renderRedesignMetaItems(metricMetaItems)}
        </Box>
      ) : null}
    </Stack>
  );
};

function getPlanningOutcomeEtaMapKey(item: WorkstreamMetric | JiraObject): string | null {
  if (isJiraObjectLike(item)) {
    return item.key ? planningMapKeys.embedOutcome.jiraEta(item.key) : null;
  }
  const id = item.id;
  if (typeof id !== 'number' || id <= 0) {
    return null;
  }
  return planningMapKeys.embedOutcome.eta(id);
}

const RedesignPlanningCommentableOutcomeEta = ({
  item,
  dueLabel,
  shouldHighlightEta,
  planningCollabOutcomeComments,
  commentMentions = [],
  fallbackTextSize = 'xsmall',
}: {
  item: WorkstreamMetric | JiraObject;
  dueLabel: string;
  shouldHighlightEta: boolean;
  planningCollabOutcomeComments?: PlanningCollabOutcomeCommentsConfig;
  commentMentions?: EmployeeMentionTypeaheadOption[];
  fallbackTextSize?: RedesignMetaTextSize;
}) => {
  const etaHandler = planningCollabOutcomeComments?.handleOutcomeEtaEditorStateChange;
  const mapKey = getPlanningOutcomeEtaMapKey(item);
  const canUsePlanningCollabEta =
    Boolean(planningCollabOutcomeComments) && Boolean(etaHandler) && Boolean(mapKey);

  if (!canUsePlanningCollabEta || !planningCollabOutcomeComments || !etaHandler || !mapKey) {
    return (
      <Text
        size={fallbackTextSize}
        color={shouldHighlightEta ? 'error' : 'subtle'}
        className={styles.redesignOutcomeMeta}
      >
        {dueLabel}
      </Text>
    );
  }

  const mapJson = planningCollabOutcomeComments.initialContentEditorState;
  const planningEtaValue = resolveStampedEditorStateMapValue(mapJson, mapKey, dueLabel);

  return (
    <Box className={styles.redesignOutcomeMeta}>
      <Suspense
        fallback={
          <Text size={fallbackTextSize} color={shouldHighlightEta ? 'error' : 'subtle'}>
            {dueLabel}
          </Text>
        }
      >
        <PlanningCommentableRichText
          key={`${mapKey}-eta-${dueLabel}`}
          value={planningEtaValue}
          editable={false}
          hideToolbar
          disableBorder
          minHeight={0}
          editablePaddingX="0"
          editablePaddingY="0"
          squareBorders
          textColor="subtle"
          textSize="small"
          contentClassName={shouldHighlightEta ? styles.redesignOutcomeMetaEtaOverdue : undefined}
          commentMentions={commentMentions}
          onChange={(state) => {
            etaHandler(mapKey, state.toString(), dueLabel);
          }}
        />
      </Suspense>
    </Box>
  );
};

const RedesignPlanningCommentableOutcomeMetaRow = ({
  item,
  dueLabel,
  ownerName,
  shouldHighlightEta,
  planningCollabOutcomeComments,
  commentMentions = [],
  textSize = 'small',
}: {
  item: WorkstreamMetric | JiraObject;
  dueLabel: string;
  ownerName?: string;
  shouldHighlightEta: boolean;
  planningCollabOutcomeComments?: PlanningCollabOutcomeCommentsConfig;
  commentMentions?: EmployeeMentionTypeaheadOption[];
  textSize?: 'small' | 'medium';
}) => {
  const planningId = usePlanningId();
  const persistHandler = planningCollabOutcomeComments?.handleOutcomeEtaEditorStateChange;
  const mapKey = planningMapKeys.embedOutcome.outcomeMeta(getOutcomeKey(item));
  const lines = [
    buildStructuredCommentSummaryLine([
      {
        id: 'due',
        text: dueLabel,
        style: shouldHighlightEta ? REDESIGN_OVERDUE_META_TEXT_STYLE : undefined,
      },
      {id: 'owner', text: ownerName},
    ]),
  ].filter((line) => line.length > 0);
  const canUse =
    Boolean(planningCollabOutcomeComments) && Boolean(persistHandler) && lines.length > 0;

  if (!canUse || !planningCollabOutcomeComments || !persistHandler) {
    return null;
  }

  return (
    <PlanningStructuredCommentField
      key={`${getOutcomeKey(item)}-outcomeMeta`}
      planningId={planningId ?? undefined}
      mapKey={mapKey}
      mapJson={planningCollabOutcomeComments.initialContentEditorState}
      lines={lines}
      editable={false}
      hideToolbar
      disableBorder
      minHeight={0}
      editablePaddingX="0"
      editablePaddingY="0"
      squareBorders
      textColor="subtle"
      textSize={textSize}
      contentClassName={styles.redesignMetaRowStructuredContent}
      selectionControls={['comments']}
      commentMentions={commentMentions}
      onPersistKey={persistHandler}
    />
  );
};

const RedesignPlanningCommentableOutcomeDetails = ({
  item,
  titleDisplay,
  summaryDisplay,
  summarySegments,
  metaAccessory,
  planningCollabOutcomeComments,
  commentMentions = [],
  mapKey = planningMapKeys.embedOutcome.outcomeDetails(getOutcomeKey(item)),
  contentClassName = styles.redesignOutcomeTypeaheadDetailsContent,
  titleTextSize = 'medium',
  summaryTextSize = 'small',
}: {
  item: WorkstreamMetric | JiraObject;
  titleDisplay: string;
  summaryDisplay: string;
  summarySegments: StructuredCommentSegmentPart[];
  metaAccessory?: ReactNode;
  planningCollabOutcomeComments?: PlanningCollabOutcomeCommentsConfig;
  commentMentions?: EmployeeMentionTypeaheadOption[];
  mapKey?: string;
  contentClassName?: string;
  titleTextSize?: 'small' | 'medium';
  summaryTextSize?: 'xsmall' | 'small';
}) => {
  const planningId = usePlanningId();
  const persistHandler = planningCollabOutcomeComments?.handleOutcomeEtaEditorStateChange;
  const summaryLine = buildStructuredCommentSummaryLine(summarySegments);
  const lines = [
    [structuredCommentSegment('title', titleDisplay)],
    ...(summaryLine.length > 0 ? [summaryLine] : []),
  ];
  const canUse =
    Boolean(planningCollabOutcomeComments) &&
    Boolean(persistHandler) &&
    titleDisplay.trim().length > 0;
  const accessoryWrapperRef = useRef<HTMLDivElement>(null);
  const metaLineElementRef = useRef<HTMLElement | null>(null);
  const metaLineIndex = lines.length - 1;
  const [metaAccessoryTop, setMetaAccessoryTop] = useState<number | null>(null);
  const updateMetaAccessoryPosition = useCallback(() => {
    const wrapper = accessoryWrapperRef.current;
    const contentSelectorClassName = contentClassName.split(' ').find(Boolean);
    const content = contentSelectorClassName
      ? wrapper?.querySelector(`.${contentSelectorClassName}`)
      : null;
    const metaLineElement = content?.children.item(metaLineIndex) as HTMLElement | null;

    if (metaLineElementRef.current && metaLineElementRef.current !== metaLineElement) {
      metaLineElementRef.current.classList.remove(styles.redesignOutcomeTitleMetaLineWithAccessory);
    }
    metaLineElementRef.current = metaLineElement;

    if (!wrapper || !metaLineElement) {
      setMetaAccessoryTop(null);
      return;
    }

    metaLineElement.classList.add(styles.redesignOutcomeTitleMetaLineWithAccessory);

    const wrapperRect = wrapper.getBoundingClientRect();
    const metaLineRect = metaLineElement.getBoundingClientRect();
    setMetaAccessoryTop(metaLineRect.top - wrapperRect.top + (metaLineRect.height - 16) / 2);
  }, [contentClassName, metaLineIndex]);

  useLayoutEffect(() => {
    if (!metaAccessory) {
      return;
    }

    updateMetaAccessoryPosition();
    const frameId = window.requestAnimationFrame(updateMetaAccessoryPosition);
    const wrapper = accessoryWrapperRef.current;
    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => updateMetaAccessoryPosition());

    if (wrapper) {
      resizeObserver?.observe(wrapper);
    }
    window.addEventListener('resize', updateMetaAccessoryPosition);

    return () => {
      window.cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateMetaAccessoryPosition);
      metaLineElementRef.current?.classList.remove(
        styles.redesignOutcomeTitleMetaLineWithAccessory
      );
    };
  }, [metaAccessory, updateMetaAccessoryPosition]);

  if (!canUse || !planningCollabOutcomeComments || !persistHandler) {
    return (
      <Box display="flex" flexDirection="column">
        <Text size={titleTextSize} className={styles.redesignMetricsTypeaheadDetailsTitle}>
          {titleDisplay}
        </Text>
        {metaAccessory ? (
          <Box className={styles.redesignOutcomeMetaRow}>
            {metaAccessory}
            {metaAccessory && summaryDisplay ? (
              <span className={styles.redesignMetaSpacer} aria-hidden="true" />
            ) : null}
            {summaryDisplay ? (
              <Text size={summaryTextSize} color="subtle">
                {summaryDisplay}
              </Text>
            ) : null}
          </Box>
        ) : summaryDisplay ? (
          <Text size={summaryTextSize} color="subtle">
            {summaryDisplay}
          </Text>
        ) : null}
      </Box>
    );
  }

  const mapJson = planningCollabOutcomeComments.initialContentEditorState;

  const field = (
    <PlanningStructuredCommentField
      key={`${getOutcomeKey(item)}-outcomeDetails`}
      planningId={planningId ?? undefined}
      mapKey={mapKey}
      mapJson={mapJson}
      lines={lines}
      editable={false}
      hideToolbar
      disableBorder
      minHeight={0}
      editablePaddingX="0"
      editablePaddingY="0"
      squareBorders
      textColor="inherit"
      textSize="small"
      contentClassName={cx(styles.redesignMetricsTypeaheadTitleContent, contentClassName)}
      selectionControls={['styling', 'highlighting', 'comments']}
      commentMentions={commentMentions}
      onPersistKey={persistHandler}
    />
  );

  if (!metaAccessory) {
    return field;
  }

  return (
    <Box ref={accessoryWrapperRef} className={styles.redesignOutcomeTitleMetaWithAccessory}>
      <span
        className={styles.redesignOutcomeTitleMetaLineAccessory}
        style={metaAccessoryTop == null ? {visibility: 'hidden'} : {top: metaAccessoryTop}}
        aria-hidden="true"
      >
        {metaAccessory}
      </span>
      {field}
    </Box>
  );
};

type RedesignOutcomeRowTooltipPlacements = {
  title: OverlayPlacement;
  status: OverlayPlacement;
  completionPrompt: OverlayPlacement;
};
const REDESIGN_OUTCOME_TITLE_TOOLTIP_MAX_WIDTH = 480;

const getRedesignOutcomeRowTooltipPlacements = (
  titlePlacement: RedesignOutcomeTooltipPlacement
): RedesignOutcomeRowTooltipPlacements => {
  const isBottom = String(titlePlacement).startsWith('bottom');
  return {
    title: titlePlacement,
    status: isBottom ? 'bottom' : 'top',
    completionPrompt: isBottom ? 'bottom-end' : 'top-end',
  };
};

const RedesignOutcomeItem = ({
  workstreamId,
  item,
  owner,
  variant = 'table',
  displayMode = 'default',
  outcomeTooltipPlacement,
  handleMetricChange,
  commentMentions = [],
  planningCollabOutcomeComments,
  combineOutcomeTitleAndMetaComments = false,
  completionUrlPromptOpen = false,
  onCompletionUrlPromptOpenChange,
  titleLines,
}: {
  workstreamId: number;
  item: WorkstreamMetric | JiraObject;
  owner?: {name?: string | null};
  variant?: RedesignOutcomesListVariant;
  // Clamps the title (full text in a tooltip); table variants clamp to 2 already.
  titleLines?: number;
  displayMode?: RedesignOutcomesListDisplayMode;
  outcomeTooltipPlacement?: RedesignOutcomeTooltipPlacement;
  handleMetricChange?: WorkstreamMetricChangeHandler;
  commentMentions?: EmployeeMentionTypeaheadOption[];
  planningCollabOutcomeComments?: PlanningCollabOutcomeCommentsConfig;
  combineOutcomeTitleAndMetaComments?: boolean;
  completionUrlPromptOpen?: boolean;
  onCompletionUrlPromptOpenChange?: (isOpen: boolean) => void;
}) => {
  const isUpdateModalVariant = variant === 'table-in-update-modal';
  const isTableVariant = variant !== 'details';
  const outcomeTooltipPlacements = getRedesignOutcomeRowTooltipPlacements(
    outcomeTooltipPlacement ?? 'top-start'
  );

  const isJira = isJiraObjectLike(item);
  const isTypeaheadNodeDisplay = displayMode === 'typeahead-node';
  const metaTextSize: RedesignMetaTextSize = isTypeaheadNodeDisplay ? 'small' : 'xsmall';
  const [isOutcomeTitleTooltipOpen, setIsOutcomeTitleTooltipOpen] = useState(false);
  const [outcomeTitleTruncated, setOutcomeTitleTruncated] = useState(false);
  const metricCompletionUrl = !isJiraObjectLike(item) ? (item.url ?? '') : '';
  const trimmedCompletionUrl = metricCompletionUrl.trim();
  const normalizedCompletionUrl = trimmedCompletionUrl
    ? normalizeExternalUrl(trimmedCompletionUrl)
    : null;
  const metricComplete = !isJiraObjectLike(item) ? item.complete : null;
  const [completionUrl, setCompletionUrl] = useState(metricCompletionUrl);
  const [completionUrlError, setCompletionUrlError] = useState<string>();
  const completionUrlInputRef = useRef<TextInputRefObject>(null);
  const outcomeTitleTooltipTriggerRef = useRef<HTMLDivElement>(null);
  const completionPromptTriggerRef = useRef<HTMLDivElement>(null);
  const isMilestone = !isJiraObjectLike(item) && item.type === 'milestone';
  const isDeliverable = !isJiraObjectLike(item) && item.type === 'deliverable';
  const completionUrlPlaceholder = t('milestone_url_placeholder').toString();
  const isOutcomeWithCompletionLink = isDeliverable || isMilestone;
  const isPrimaryOutcome = isPrimaryOutcomeForWorkstream(item, workstreamId);
  const shouldWrapAccessoryBelowTitle = isDeliverable || (isJira && Boolean(item.status));
  const outcomeTitleText = getPlainTextTitle(item.title);
  const outcomeRowKey = getOutcomeKey(item);
  const resolvedTitleLines = titleLines ?? (isTableVariant ? 2 : undefined);
  const showOutcomeTitleTooltip = resolvedTitleLines != null && Boolean(outcomeTitleText.trim());
  const onOutcomeTitleTruncateOverflowChange = useCallback((next: boolean) => {
    setOutcomeTitleTruncated(next);
  }, []);

  useEffect(() => {
    setOutcomeTitleTruncated(false);
  }, [outcomeRowKey]);

  useEffect(() => {
    if (!showOutcomeTitleTooltip) {
      setOutcomeTitleTruncated(false);
    }
  }, [showOutcomeTitleTooltip]);

  const outcomeMetaParts = getOutcomeMetaParts(item, owner);
  const outcomeStatusFromItem: OutcomeStatus = !isJiraObjectLike(item)
    ? getOutcomeStatus(item)
    : 'open';
  const [outcomeStatus, setOutcomeStatus] = useState(() => outcomeStatusFromItem);
  const legacyDeliverableStatusFromItem: LegacyOutcomeStatusValue = isDeliverable
    ? getLegacyDeliverableStatusValue(item)
    : 'not_done';
  const legacyMilestoneStatusFromItem: LegacyOutcomeStatusValue = isMilestone
    ? getLegacyMilestoneStatusValue(item)
    : 'not_done';
  const [legacyDeliverableStatus, setLegacyDeliverableStatus] = useState(
    () => legacyDeliverableStatusFromItem
  );
  const [legacyMilestoneStatus, setLegacyMilestoneStatus] = useState(
    () => legacyMilestoneStatusFromItem
  );
  void legacyDeliverableStatus;
  void legacyMilestoneStatus;
  const [isOutcomeNarrow, setIsOutcomeNarrow] = useState(false);
  const isOutcomeTerminal = isTerminalOutcomeStatus(outcomeStatus);
  const isOutcomeClosed = isDeliverable
    ? isOutcomeTerminal
    : isMilestone
      ? isOutcomeTerminal
      : isCompletedOutcome(item);
  const completionLinkUrl =
    isOutcomeWithCompletionLink && isOutcomeTerminal ? normalizedCompletionUrl : null;
  // Milestones show only the open-link icon; deliverables keep the "View" label.
  const completionLinkLabel = completionLinkUrl && isDeliverable ? t('view').toString() : undefined;
  const shouldHighlightEta = shouldHighlightOutcomeEta(outcomeMetaParts.isOverdue, isOutcomeClosed);
  const outcomeItemRef = useRef<HTMLDivElement | null>(null);
  const showFullOutcomeStatusChip = variant === 'details';
  const jiraStatusTooltipTitle = isJira ? getJiraOutcomeStatusTooltipTitle(item) : undefined;
  // A lifecycle-only cancel (status still e.g. "On Track") reads as Cancelled,
  // matching the Closed group it's in.
  const jiraChipStatus = isJira
    ? isCancelledJiraOutcome(item)
      ? 'Cancelled'
      : (item.status ?? undefined)
    : undefined;
  const outcomeStatusClassName = cx(
    styles.redesignOutcomeMetaStatus,
    variant === 'details' && styles.redesignOutcomeMetaStatusDetails,
    showFullOutcomeStatusChip && styles.redesignOutcomeMetaStatusChip
  );
  const outcomeSummaryDisplay = useMemo(
    () =>
      [
        outcomeMetaParts.dueLabel,
        outcomeMetaParts.ownerName,
        outcomeMetaParts.jiraKey,
        completionLinkLabel,
      ]
        .filter((part): part is string => Boolean(part))
        .join(' • '),
    [
      completionLinkLabel,
      outcomeMetaParts.dueLabel,
      outcomeMetaParts.jiraKey,
      outcomeMetaParts.ownerName,
    ]
  );
  const outcomeTitleAndMetaSummaryDisplay = useMemo(
    () =>
      [getRedesignOutcomeTypeLabel(item), outcomeMetaParts.dueLabel, outcomeMetaParts.ownerName]
        .filter((part): part is string => Boolean(part))
        .join(' • '),
    [item, outcomeMetaParts.dueLabel, outcomeMetaParts.ownerName]
  );
  const outcomeTitleAndMetaTitleDisplay = outcomeTitleText || item.title;
  const primaryOutcomeMetaAccessory = isPrimaryOutcome ? (
    <span
      className={cx(styles.redesignOutcomePrimaryIcon, styles.redesignOutcomePrimaryIconSlot)}
      aria-hidden="true"
    >
      <UIIcon src={StarLine} size="small" />
    </span>
  ) : null;
  const outcomeMetaItems = useMemo(() => {
    const items: RedesignMetaItem[] = [];

    if (isTypeaheadNodeDisplay) {
      return [];
    }

    if (isPrimaryOutcome && !isTypeaheadNodeDisplay) {
      items.push({
        key: 'primary',
        kind: 'icon',
        content: (
          <span className={styles.redesignOutcomePrimaryIcon} aria-hidden="true">
            <UIIcon src={StarLine} size="small" />
          </span>
        ),
      });
    }

    if (!isTypeaheadNodeDisplay) {
      items.push({
        key: 'type',
        kind: 'icon',
        content: (
          <UIIcon
            src={getOutcomeIcon(item)}
            size="small"
            className={styles.redesignOutcomeMetaTypeIcon}
          />
        ),
      });
    }

    if (planningCollabOutcomeComments) {
      items.push({
        key: 'meta',
        kind: 'text',
        content: (
          <RedesignPlanningCommentableOutcomeMetaRow
            item={item}
            dueLabel={outcomeMetaParts.dueLabel}
            ownerName={outcomeMetaParts.ownerName}
            shouldHighlightEta={shouldHighlightEta}
            planningCollabOutcomeComments={planningCollabOutcomeComments}
            commentMentions={commentMentions}
            textSize={metaTextSize === 'xsmall' ? 'small' : 'medium'}
          />
        ),
      });
    } else {
      items.push({
        key: 'due',
        kind: 'text',
        content: (
          <RedesignPlanningCommentableOutcomeEta
            item={item}
            dueLabel={outcomeMetaParts.dueLabel}
            shouldHighlightEta={shouldHighlightEta}
            planningCollabOutcomeComments={planningCollabOutcomeComments}
            commentMentions={commentMentions}
            fallbackTextSize={metaTextSize}
          />
        ),
      });

      if (outcomeMetaParts.ownerName) {
        const ownerMetric = !isJiraObjectLike(item) ? (item as WorkstreamMetric) : null;
        items.push({
          key: 'owner',
          kind: 'text',
          content: ownerMetric ? (
            <RedesignPlanningCommentableMetricOwner
              metric={ownerMetric}
              ownerDisplayName={outcomeMetaParts.ownerName}
              planningCollabOutcomeComments={planningCollabOutcomeComments}
              commentMentions={commentMentions}
              contentClassName={cx(styles.redesignOutcomeMeta, styles.redesignOutcomeOwnerMeta)}
              fallbackTextSize={metaTextSize}
            />
          ) : (
            <Text
              size={metaTextSize}
              color="subtle"
              className={cx(styles.redesignOutcomeMeta, styles.redesignOutcomeOwnerMeta)}
            >
              {outcomeMetaParts.ownerName}
            </Text>
          ),
        });
      }
    }

    if (outcomeMetaParts.jiraKey) {
      items.push({
        key: 'jira',
        kind: 'text',
        content: isJiraObjectLike(item) ? (
          <Link
            to={`https://dropbox.atlassian.net/browse/${item.key}`}
            target="_blank"
            monochromatic
            hasNoUnderline
            className={styles.redesignOutcomeMetaLink}
          >
            <Text size={metaTextSize} color="subtle">
              {outcomeMetaParts.jiraKey}
            </Text>
          </Link>
        ) : (
          <Text size={metaTextSize} color="subtle" className={styles.redesignOutcomeMeta}>
            {outcomeMetaParts.jiraKey}
          </Text>
        ),
      });
    }

    if (completionLinkUrl) {
      items.push({
        key: 'view',
        kind: completionLinkLabel ? 'text' : 'icon',
        content: (
          <Link
            to={completionLinkUrl}
            target="_blank"
            monochromatic
            hasNoUnderline
            className={styles.redesignOutcomeMetaLink}
            aria-label={t('view_milestone_for', {
              name: outcomeTitleText || t('milestone').toString(),
            }).toString()}
          >
            {completionLinkLabel ? (
              <Text size={metaTextSize} color="subtle">
                {completionLinkLabel}
              </Text>
            ) : null}
            <UIIcon src={OpenLine} size="small" className={styles.redesignOutcomeOpenIcon} />
          </Link>
        ),
      });
    }

    return items;
  }, [
    completionLinkLabel,
    completionLinkUrl,
    isPrimaryOutcome,
    isTypeaheadNodeDisplay,
    item,
    metaTextSize,
    outcomeMetaParts.dueLabel,
    outcomeMetaParts.jiraKey,
    outcomeMetaParts.ownerName,
    outcomeTitleText,
    shouldHighlightEta,
    planningCollabOutcomeComments,
    commentMentions,
  ]);
  const outcomeSupplementalMetaItems = combineOutcomeTitleAndMetaComments
    ? outcomeMetaItems.filter((metaItem) => metaItem.key === 'jira' || metaItem.key === 'view')
    : [];

  useEffect(() => {
    if (!completionUrlPromptOpen) {
      setCompletionUrl(metricCompletionUrl);
      setCompletionUrlError(undefined);
    }
  }, [completionUrlPromptOpen, metricCompletionUrl]);

  useEffect(() => {
    if (isDeliverable || isMilestone) {
      setOutcomeStatus((previous) =>
        previous === outcomeStatusFromItem ? previous : outcomeStatusFromItem
      );
    }
  }, [isDeliverable, isMilestone, item, metricComplete, outcomeStatusFromItem]);

  useEffect(() => {
    if (isDeliverable) {
      setLegacyDeliverableStatus((previous) =>
        previous === legacyDeliverableStatusFromItem ? previous : legacyDeliverableStatusFromItem
      );
    }
  }, [isDeliverable, item, legacyDeliverableStatusFromItem, metricComplete]);

  useEffect(() => {
    if (isMilestone) {
      setLegacyMilestoneStatus((previous) =>
        previous === legacyMilestoneStatusFromItem ? previous : legacyMilestoneStatusFromItem
      );
    }
  }, [isMilestone, item, legacyMilestoneStatusFromItem, metricComplete]);

  useEffect(() => {
    const element = outcomeItemRef.current;
    if (!element) return;

    const updateIsOutcomeNarrow = (width: number) => {
      const nextIsOutcomeNarrow = width < 350;
      setIsOutcomeNarrow((previous) =>
        previous === nextIsOutcomeNarrow ? previous : nextIsOutcomeNarrow
      );
    };

    updateIsOutcomeNarrow(element.getBoundingClientRect().width);

    if (typeof ResizeObserver === 'undefined') return;

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      updateIsOutcomeNarrow(entry.contentRect.width);
    });
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const handleCompletionPromptSubmit = useCallback(() => {
    if (!handleMetricChange || isJiraObjectLike(item)) return false;
    const trimmedUrl = completionUrl.trim();
    if (!trimmedUrl) {
      return false;
    }

    const normalizedUrl = normalizeCompletionUrl(trimmedUrl);
    if (!normalizedUrl) {
      setCompletionUrlError(t('manage_outcomes_error_valid_url').toString());
      return false;
    }

    if (isOutcomeWithCompletionLink) {
      void handleMetricChange(item.id, {
        ...getOutcomeStatusUpdate('completed'),
        url: normalizedUrl,
      });
      return true;
    }
    return false;
  }, [completionUrl, handleMetricChange, isOutcomeWithCompletionLink, item]);

  useEffect(() => {
    if (completionUrlPromptOpen && completionUrlInputRef.current) {
      // Delay focus to completion URL input to allow the popover to finish positioning.
      const timeoutId = setTimeout(() => {
        completionUrlInputRef.current?.focus({preventScroll: true});
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [completionUrlPromptOpen]);

  const handleOutcomeStatusChange = useCallback(
    (update: OutcomeStatusUpdate) => {
      if (!handleMetricChange || isJiraObjectLike(item)) return;
      const nextStatus = getOutcomeStatus(update);
      setOutcomeStatus(nextStatus);
      void handleMetricChange(item.id, update);

      if (
        isOutcomeWithCompletionLink &&
        outcomeStatus !== 'completed' &&
        nextStatus === 'completed'
      ) {
        setCompletionUrl(metricCompletionUrl);
        onCompletionUrlPromptOpenChange?.(true);
      }
    },
    [
      handleMetricChange,
      isOutcomeWithCompletionLink,
      item,
      metricCompletionUrl,
      onCompletionUrlPromptOpenChange,
      outcomeStatus,
    ]
  );

  const handleLegacyMilestoneStatusChange = useCallback(
    (status: LegacyOutcomeStatusValue) => {
      if (!handleMetricChange || isJiraObjectLike(item) || item.type !== 'milestone') return;
      setLegacyMilestoneStatus(status);
      void handleMetricChange(
        item.id,
        getOutcomeStatusUpdate(status === 'done' ? 'completed' : 'open')
      );
    },
    [handleMetricChange, item]
  );

  const handleLegacyDeliverableStatusChange = useCallback(
    (status: LegacyOutcomeStatusValue) => {
      if (!handleMetricChange || isJiraObjectLike(item) || item.type !== 'deliverable') return;
      if (status === 'done') {
        setLegacyDeliverableStatus(status);
        setCompletionUrl(metricCompletionUrl);
        onCompletionUrlPromptOpenChange?.(true);
        return;
      }
      setLegacyDeliverableStatus(status);
      void handleMetricChange(item.id, getOutcomeStatusUpdate('open'));
    },
    [handleMetricChange, item, metricCompletionUrl, onCompletionUrlPromptOpenChange]
  );
  void handleLegacyMilestoneStatusChange;
  void handleLegacyDeliverableStatusChange;

  const outcomeStatusControl =
    isDeliverable || (isMilestone && !isTypeaheadNodeDisplay) ? (
      <Box
        className={cx(
          outcomeStatusClassName,
          styles.redesignOutcomeMilestoneStatus,
          styles.redesignOutcomeStatusEnabled,
          isTableVariant && styles.redesignOutcomeStatusCompact,
          isUpdateModalVariant && styles.redesignOutcomeModalStatus
        )}
        ref={completionPromptTriggerRef}
      >
        <OutcomeStatusControl
          complete={!isJiraObjectLike(item) ? item.complete : false}
          compact={isTableVariant && !isUpdateModalVariant}
          mediumIconOnly={isUpdateModalVariant}
          status={outcomeStatus}
          onChange={handleOutcomeStatusChange}
          disabled={!handleMetricChange}
        />
      </Box>
    ) : isJira && item.status ? (
      <Box className={outcomeStatusClassName}>
        {showFullOutcomeStatusChip ? (
          <span aria-label={getOutcomeStatusLabel(item)} title={jiraStatusTooltipTitle}>
            <StatusChip
              status={jiraChipStatus}
              asStatus={getJiraOutcomeButtonStatus(jiraChipStatus)}
              size="xsmall"
              includeArchived
            />
          </span>
        ) : (
          <OptionalTooltip
            enabled
            title={jiraStatusTooltipTitle}
            placement={outcomeTooltipPlacements.status}
            offsetDistance={-6}
            wrapperClassName={styles.redesignOutcomeStatusTrigger}
          >
            <ReadOnlyOutcomeStatusButton
              status={getJiraOutcomeButtonStatus(jiraChipStatus)}
              label={getOutcomeStatusLabel(item)}
              includeArchived
            />
          </OptionalTooltip>
        )}
      </Box>
    ) : null;

  return (
    <>
      {showOutcomeTitleTooltip ? (
        <Tooltip.Control
          open={isOutcomeTitleTooltipOpen && outcomeTitleTruncated}
          triggerRef={outcomeTitleTooltipTriggerRef}
          placement={outcomeTooltipPlacements.title}
          auto
          contentBreak="word"
          maxWidth={REDESIGN_OUTCOME_TITLE_TOOLTIP_MAX_WIDTH}
        >
          <Text size="small" className={styles.redesignOutcomeTitleTooltipContent}>
            {outcomeTitleText}
          </Text>
        </Tooltip.Control>
      ) : null}
      <Popover
        anchorEl={completionPromptTriggerRef.current}
        closeOnOutsideClick={false}
        open={completionUrlPromptOpen}
        placement={outcomeTooltipPlacements.completionPrompt}
        paddingX={16}
        paddingY={16}
        paddingBottom={14}
        onToggle={(isOpen) => {
          if (isOpen) {
            onCompletionUrlPromptOpenChange?.(true);
          }
        }}
        withArrow
      >
        <Popover.Overlay>
          <Popover.Content>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                if (handleCompletionPromptSubmit()) {
                  onCompletionUrlPromptOpenChange?.(false);
                }
              }}
            >
              <Popover.Body>
                <Stack gap="12" style={{width: 'min(280px, calc(100vw - 64px))'}}>
                  <TextInput
                    // Use ref to focus on input since autoFocus may cause page to scroll
                    ref={completionUrlInputRef}
                    type="text"
                    size="small"
                    aria-label={completionUrlPlaceholder}
                    placeholder={completionUrlPlaceholder}
                    isInvalid={Boolean(completionUrlError)}
                    value={completionUrl}
                    onChange={(event) => {
                      setCompletionUrl(event.target.value);
                      setCompletionUrlError(undefined);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        if (handleCompletionPromptSubmit()) {
                          onCompletionUrlPromptOpenChange?.(false);
                        }
                      }
                    }}
                  />
                  {completionUrlError ? (
                    <Text size="small" color="error" role="alert">
                      {completionUrlError}
                    </Text>
                  ) : null}
                  <Box marginTop="4" display="flex" justifyContent="flex-end" style={{gap: 8}}>
                    <CancelButton
                      type="button"
                      size="small"
                      onClick={() => onCompletionUrlPromptOpenChange?.(false)}
                    >
                      {t('skip').toString()}
                    </CancelButton>
                    <Button type="submit" variant="primary" size="small">
                      {t('save').toString()}
                    </Button>
                  </Box>
                </Stack>
              </Popover.Body>
            </form>
          </Popover.Content>
        </Popover.Overlay>
      </Popover>
      <Box
        ref={outcomeItemRef}
        className={cx(styles.redesignOutcomeItem, {
          [styles.redesignOutcomeItemNarrow]: isOutcomeNarrow,
          [styles.redesignOutcomeItemDetails]: variant === 'details',
          [styles.redesignOutcomeItemTypeaheadNode]: isTypeaheadNodeDisplay,
        })}
      >
        {outcomeStatusControl}
        <Box
          className={cx(styles.redesignOutcomeBody, {
            [styles.redesignOutcomeBodyWrapped]: isOutcomeNarrow && shouldWrapAccessoryBelowTitle,
          })}
        >
          <Box className={styles.redesignOutcomeHeaderRow}>
            <Box
              ref={showOutcomeTitleTooltip ? outcomeTitleTooltipTriggerRef : undefined}
              className={styles.redesignOutcomeTitle}
              onMouseEnter={
                showOutcomeTitleTooltip ? () => setIsOutcomeTitleTooltipOpen(true) : undefined
              }
              onMouseLeave={
                showOutcomeTitleTooltip ? () => setIsOutcomeTitleTooltipOpen(false) : undefined
              }
            >
              {isTypeaheadNodeDisplay ? (
                <RedesignPlanningCommentableOutcomeDetails
                  item={item}
                  titleDisplay={outcomeTitleText || item.title}
                  summaryDisplay={outcomeSummaryDisplay}
                  summarySegments={[
                    {
                      id: 'due',
                      text: outcomeMetaParts.dueLabel,
                      style: shouldHighlightEta ? REDESIGN_OVERDUE_META_TEXT_STYLE : undefined,
                    },
                    {id: 'owner', text: outcomeMetaParts.ownerName},
                    {id: 'jira', text: outcomeMetaParts.jiraKey},
                    {
                      id: 'view',
                      text: completionLinkLabel,
                    },
                  ]}
                  planningCollabOutcomeComments={planningCollabOutcomeComments}
                  commentMentions={commentMentions}
                />
              ) : combineOutcomeTitleAndMetaComments ? (
                <RedesignPlanningCommentableOutcomeDetails
                  item={item}
                  titleDisplay={outcomeTitleAndMetaTitleDisplay}
                  summaryDisplay={outcomeTitleAndMetaSummaryDisplay}
                  summarySegments={[
                    {id: 'type', text: getRedesignOutcomeTypeLabel(item)},
                    {
                      id: 'due',
                      text: outcomeMetaParts.dueLabel,
                      style: shouldHighlightEta ? REDESIGN_OVERDUE_META_TEXT_STYLE : undefined,
                    },
                    {id: 'owner', text: outcomeMetaParts.ownerName},
                  ]}
                  metaAccessory={primaryOutcomeMetaAccessory ?? undefined}
                  planningCollabOutcomeComments={planningCollabOutcomeComments}
                  commentMentions={commentMentions}
                  mapKey={planningMapKeys.embedOutcome.outcomeTitleAndMeta(getOutcomeKey(item))}
                  contentClassName={styles.redesignOutcomeTitleAndMetaContent}
                  titleTextSize="small"
                  summaryTextSize="xsmall"
                />
              ) : (
                <RedesignCommentableTitle
                  item={item}
                  commentMentions={commentMentions}
                  truncateLines={resolvedTitleLines}
                  planningCollabOutcomeComments={planningCollabOutcomeComments}
                  titleTypography={isUpdateModalVariant ? {textSize: 'medium'} : undefined}
                  titleWrapperClassName={
                    isUpdateModalVariant
                      ? styles.redesignInlineEditableContentUpdateModal
                      : undefined
                  }
                  onTitleTruncateOverflowChange={
                    showOutcomeTitleTooltip ? onOutcomeTitleTruncateOverflowChange : undefined
                  }
                />
              )}
            </Box>
          </Box>
          {!isTypeaheadNodeDisplay && !combineOutcomeTitleAndMetaComments ? (
            <Box className={styles.redesignOutcomeMetaRow}>
              {renderRedesignMetaItems(outcomeMetaItems)}
            </Box>
          ) : outcomeSupplementalMetaItems.length > 0 ? (
            <Box className={styles.redesignOutcomeMetaRow}>
              {renderRedesignMetaItems(outcomeSupplementalMetaItems)}
            </Box>
          ) : null}
        </Box>
      </Box>
    </>
  );
};

export const RedesignMetricsList = ({
  workstreamId,
  workstreamName,
  metrics,
  allEditableMetrics,
  jiraOutcomes = [],
  owner,
  selectedPeriod,
  selectedForecastType,
  metricPeriodOptions,
  commentMentions = [],
  handleMetricChange,
  statusChangeSource,
  onAttachMetric,
  onDelete,
  loadElevatableJiraOutcomes,
  onElevateJiraOutcome,
  onDetachJiraOutcome,
  isPending = false,
  disableCreateMetric = false,
  onExpandRow,
  onSectionExpandedChange,
  variant = 'table',
  planningCollabOutcomeComments,
  displayMode = 'default',
  typeaheadSectionLabel,
  typeaheadSummaryMode = 'currentTarget',
  showTypeaheadMetricStatus = true,
  showTypeaheadSectionLabel = true,
  showTypeaheadMnpiMetricIcon = false,
  combineMetricTitleAndMetaComments = false,
  showAllMetricsByDefault = false,
  showNorthStarMetricIcon = false,
  showBaselineColumn = false,
  hideForecastColumn = false,
  actualPeriod,
  hideHeader = false,
  showTouchCurrentUpdatedAtControls = false,
  touchCurrentUpdatedAtMetricIds,
  hiddenMnpiMetricsCount,
}: {
  workstreamId: number;
  workstreamName?: string;
  metrics: Array<DetailsTableMetric | RedactedWorkstreamMetric>;
  allEditableMetrics: WorkstreamMetric[];
  jiraOutcomes?: JiraObject[];
  owner?: {name?: string | null};
  selectedPeriod: string;
  selectedForecastType: MetricsForecastType;
  metricPeriodOptions?: string[];
  commentMentions?: EmployeeMentionTypeaheadOption[];
  planningCollabOutcomeComments?: PlanningCollabOutcomeCommentsConfig;
  handleMetricChange?: WorkstreamMetricChangeHandler;
  statusChangeSource?: WorkstreamMetricStatusChangeSource;
  onAttachMetric?: (
    attachRequest: MetricAttachRequest,
    reason?: string
  ) => Promise<WorkstreamMetric | void>;
  onDelete?: (metric: WorkstreamMetric, reason: string) => Promise<void>;
  isPending?: boolean;
  disableCreateMetric?: boolean;
  onExpandRow?: () => void;
  onSectionExpandedChange?: (expanded: boolean) => void;
  variant?: RedesignMetricsListVariant;
  displayMode?: RedesignMetricsListDisplayMode;
  typeaheadSectionLabel?: string;
  typeaheadSummaryMode?: RedesignMetricTypeaheadSummaryMode;
  showTypeaheadMetricStatus?: boolean;
  showTypeaheadSectionLabel?: boolean;
  showTypeaheadMnpiMetricIcon?: boolean;
  combineMetricTitleAndMetaComments?: boolean;
  showAllMetricsByDefault?: boolean;
  showNorthStarMetricIcon?: boolean;
  showBaselineColumn?: boolean;
  hideForecastColumn?: boolean;
  actualPeriod?: string;
  hideHeader?: boolean;
  showTouchCurrentUpdatedAtControls?: boolean;
  touchCurrentUpdatedAtMetricIds?: ReadonlySet<number>;
  hiddenMnpiMetricsCount?: number;
} & JiraElevationActions) => {
  const isDetailsVariant = variant === 'details';
  const isUpdateModalVariant = variant === 'table-in-update-modal';
  const showActualColumn = isUpdateModalVariant && Boolean(actualPeriod);
  const isTypeaheadNodeDisplay = displayMode === 'typeahead-node';
  const variantConfig = REDESIGN_METRICS_LIST_VARIANT_CONFIG[variant];
  const detailsMetricsYear = useMemo(
    () => parseMetricsPeriod(selectedPeriod)?.year ?? CURRENT_YEAR,
    [selectedPeriod]
  );
  const allSortedMetrics = useMemo(
    () =>
      metrics.every(isFullWorkstreamMetric)
        ? [...metrics].sort((a, b) => sortWorkstreamMetrics(a, b, workstreamId))
        : metrics,
    [metrics, workstreamId]
  );
  const autoExpandedMetricsRef = useRef<boolean | null>(null);
  const onSectionExpandedChangeRef = useRef(onSectionExpandedChange);
  const [showAll, setShowAll] = useState(false);
  const [showClosedMetrics, setShowClosedMetrics] = useState(false);
  const shouldGroupClosedMetrics = isDetailsVariant && !isTypeaheadNodeDisplay;
  const {sortedMetrics, closedMetrics} = useMemo(
    () =>
      shouldGroupClosedMetrics
        ? {
            sortedMetrics: allSortedMetrics.filter(
              (metric) => !isFullWorkstreamMetric(metric) || !isClosedMetricStatus(metric.status)
            ),
            closedMetrics: allSortedMetrics.filter(
              (metric) => isFullWorkstreamMetric(metric) && isClosedMetricStatus(metric.status)
            ),
          }
        : {sortedMetrics: allSortedMetrics, closedMetrics: []},
    [allSortedMetrics, shouldGroupClosedMetrics]
  );
  const primaryMetrics = useMemo(
    () =>
      sortedMetrics.filter(
        (metric) => isFullWorkstreamMetric(metric) && isPrimaryMetric(metric, workstreamId)
      ),
    [sortedMetrics, workstreamId]
  );
  const remainingMetrics = useMemo(() => {
    return sortedMetrics.filter(
      (metric) => !isFullWorkstreamMetric(metric) || !isPrimaryMetric(metric, workstreamId)
    );
  }, [sortedMetrics, workstreamId]);
  const prioritizedMetrics = useMemo(
    () => (primaryMetrics.length > 0 ? [...primaryMetrics, ...remainingMetrics] : sortedMetrics),
    [primaryMetrics, remainingMetrics, sortedMetrics]
  );
  const {northStarMetric, orderedMetrics: tableOrderedMetrics} = useMemo(
    () =>
      sortedMetrics.every(isFullWorkstreamMetric)
        ? getNorthStarMetricOrdering(sortedMetrics, workstreamId)
        : {northStarMetric: null, orderedMetrics: sortedMetrics},
    [sortedMetrics, workstreamId]
  );
  const displayMetrics = isDetailsVariant ? prioritizedMetrics : tableOrderedMetrics;
  const defaultVisibleMetrics = useMemo(
    () =>
      isDetailsVariant || showAllMetricsByDefault ? displayMetrics : displayMetrics.slice(0, 1),
    [displayMetrics, isDetailsVariant, showAllMetricsByDefault]
  );
  const remainingVisibleMetrics = useMemo(
    () =>
      isDetailsVariant || showAllMetricsByDefault
        ? []
        : displayMetrics.slice(defaultVisibleMetrics.length),
    [displayMetrics, isDetailsVariant, showAllMetricsByDefault, defaultVisibleMetrics.length]
  );
  const hiddenMetricCount =
    isDetailsVariant || showAllMetricsByDefault
      ? 0
      : Math.max(0, displayMetrics.length - defaultVisibleMetrics.length);
  const hasExpandableMetrics =
    !isDetailsVariant &&
    !showAllMetricsByDefault &&
    displayMetrics.length > defaultVisibleMetrics.length;
  const toggleShowAll = () => {
    onExpandRow?.();
    const nextExpanded = !showAll;
    onSectionExpandedChangeRef.current?.(nextExpanded && hasExpandableMetrics);
    setShowAll(nextExpanded);
  };
  useEffect(() => {
    onSectionExpandedChangeRef.current = onSectionExpandedChange;
  }, [onSectionExpandedChange]);

  useEffect(() => {
    if (hasExpandableMetrics || !showAll) {
      return;
    }

    setShowAll(false);
    if (autoExpandedMetricsRef.current !== false) {
      autoExpandedMetricsRef.current = false;
      onSectionExpandedChangeRef.current?.(false);
    }
  }, [hasExpandableMetrics, showAll]);

  const toggleClosedMetrics = useCallback(() => {
    onExpandRow?.();
    setShowClosedMetrics((previousShowClosedMetrics) => !previousShowClosedMetrics);
  }, [onExpandRow]);

  if (allSortedMetrics.length === 0) {
    return !disableCreateMetric && handleMetricChange ? (
      <RedesignZeroStateManageTrigger
        workstreamId={workstreamId}
        workstreamName={workstreamName}
        metrics={allEditableMetrics}
        jiraOutcomes={jiraOutcomes}
        initialTab="metrics"
        isPending={isPending}
        owner={owner as EmployeeRoleLevel | undefined}
        onSaveMetric={async (metricId, update, reason, existingMetric) => {
          return handleMetricChange?.(metricId, update, reason, existingMetric);
        }}
        onAttachMetric={onAttachMetric}
        onDeleteMetric={onDelete}
        loadElevatableJiraOutcomes={loadElevatableJiraOutcomes}
        onElevateJiraOutcome={onElevateJiraOutcome}
        onDetachJiraOutcome={onDetachJiraOutcome}
        metricPeriodOptions={metricPeriodOptions}
        hiddenMnpiMetricsCount={hiddenMnpiMetricsCount}
      />
    ) : null;
  }

  const renderMetricRow = (metric: DetailsTableMetric | RedactedWorkstreamMetric) => {
    if (!isFullWorkstreamMetric(metric)) {
      const identityMetric = getRedactedMetricIdentityProjection(metric);
      const metricNameData = getWorkstreamMetricNameData(identityMetric, owner);
      const redactedCells =
        variant === 'details'
          ? 2 + Number(showBaselineColumn)
          : 2 + Number(showBaselineColumn) + Number(showActualColumn) + Number(!hideForecastColumn);
      const renderRedactedMetricValue = () => <RedactedMetricValuePlaceholder centerHorizontally />;
      const renderRedactedForecastCell = () => (
        <div className={cx(styles.redesignMetricsCell, styles.redesignMetricsForecastGridCell)}>
          <div className={styles.redesignRedactedForecastGrid}>
            <div aria-hidden />
            {METRIC_FORECAST_PERIODS.map((period) => (
              <RedactedMetricValuePlaceholder key={period} centerHorizontally />
            ))}
          </div>
        </div>
      );

      return (
        <div
          key={metric.id}
          className={cx(
            styles.redesignMetricsRow,
            variant === 'details' && styles.redesignMetricsRowDetails,
            showBaselineColumn && styles.redesignMetricsRowWithBaseline,
            showActualColumn && styles.redesignMetricsRowWithActual,
            styles.redesignMetricsRowDetailsData
          )}
        >
          {variant === 'details' ? (
            <div className={styles.redesignMetricsCell}>
              <RedactedMetricValuePlaceholder />
            </div>
          ) : null}
          <div className={styles.redesignMetricsCell}>
            <div className={cx(styles.redesignMetricCellContent, styles.redesignMetricsNameCell)}>
              <WorkstreamMetricNameCellContent
                metric={identityMetric}
                nameData={metricNameData}
                redactedMetric
              />
            </div>
          </div>
          {variant === 'details' ? (
            <>
              {showBaselineColumn ? (
                <div
                  className={cx(
                    styles.redesignMetricsCell,
                    styles.redesignMetricsValueBold,
                    styles.redesignMetricsCurrentCell
                  )}
                >
                  {renderRedactedMetricValue()}
                </div>
              ) : null}
              <div
                className={cx(
                  styles.redesignMetricsCell,
                  styles.redesignMetricsValueBold,
                  styles.redesignMetricsCurrentCell
                )}
              >
                {renderRedactedMetricValue()}
              </div>
              {renderRedactedForecastCell()}
            </>
          ) : (
            Array.from({length: redactedCells}, (_, index) => (
              <div key={index} className={styles.redesignMetricsCell}>
                {renderRedactedMetricValue()}
              </div>
            ))
          )}
        </div>
      );
    }
    const metricChangeHandler = handleMetricChange;
    const metricNameData = getWorkstreamMetricNameData(metric, owner);
    const {
      confidentialTitleClassName: confidentialMetricTitleClassName,
      isMnpi: isMnpiMetric,
      ownerName,
      signal,
      titleText: metricTitleText,
    } = metricNameData;
    const baselineValueText = getMetricBaselineValue(metric);
    const compactBaselineValue = formatCompactMetricValue(baselineValueText);
    const currentValueText = getMetricCurrentValue(metric);
    const compactCurrentValue = formatCompactMetricValue(currentValueText);
    const forecastValueText = getMetricSelectedValueForPeriod(
      metric,
      selectedPeriod,
      selectedForecastType
    );
    const compactForecastValue = formatCompactMetricValue(forecastValueText);
    const metricMetaDisplay = [signal ? t(signal).toString() : undefined, ownerName]
      .filter(Boolean)
      .join(' • ');
    const isNorthStarMetric = northStarMetric?.id === metric.id;
    const shouldUseNorthStarIconSlot = showNorthStarMetricIcon && isNorthStarMetric;
    const mnpiMetricIcon = isMnpiMetric ? (
      <MnpiMetricIndicator className={styles.redesignMetricMnpiIcon} />
    ) : null;
    const northStarMetricIcon = isNorthStarMetric ? (
      <NorthStarMetricIcon
        className={shouldUseNorthStarIconSlot ? styles.redesignMetricPrimaryIconSlot : undefined}
      />
    ) : null;
    const northStarMetricMetaAccessory = metricMetaDisplay ? northStarMetricIcon : null;
    const northStarMetricMetaItem: RedesignMetaItem | null = northStarMetricMetaAccessory
      ? {
          key: 'north-star',
          kind: 'icon',
          content: northStarMetricMetaAccessory,
        }
      : null;
    const mnpiMetricMetaItem: RedesignMetaItem | null = mnpiMetricIcon
      ? {
          key: 'mnpi',
          kind: 'icon',
          content: mnpiMetricIcon,
        }
      : null;
    const metricTitleMetaAccessory =
      mnpiMetricIcon || northStarMetricMetaAccessory ? (
        <span className={styles.redesignMetricTitleMetaAccessoryGroup}>
          {mnpiMetricIcon}
          {northStarMetricMetaAccessory}
        </span>
      ) : undefined;
    const typeaheadMnpiMetricIcon =
      showTypeaheadMnpiMetricIcon && isMnpiMetric ? (
        <MnpiMetricIndicator
          className={cx(styles.redesignMetricMnpiIcon, styles.redesignMetricsTypeaheadPrimaryIcon)}
        />
      ) : null;
    const renderMetricValueDisplay = (
      compactValue: ReturnType<typeof formatCompactMetricValue>,
      valueClassName: string
    ) => {
      return (
        <OptionalTooltip
          enabled={Boolean(compactValue.tooltipValue)}
          title={<Text size="small">{compactValue.tooltipValue}</Text>}
          placement="top"
          wrapperClassName={cx(styles.redesignMetricsValueTooltipTrigger, valueClassName)}
        >
          <span
            className={cx(
              styles.redesignMetricsValueText,
              valueClassName,
              compactValue.tooltipValue && styles.redesignMetricsValueWithTooltip
            )}
          >
            {compactValue.displayValue}
          </span>
        </OptionalTooltip>
      );
    };
    if (isTypeaheadNodeDisplay) {
      const currentSummaryFallback = getMetricSummaryDisplay(
        compactCurrentValue.displayValue,
        t('current').toString(),
        t('no_current').toString()
      );
      const targetSummaryFallback = getMetricSummaryDisplay(
        compactForecastValue.displayValue,
        t('target').toString(),
        t('no_target').toString()
      );
      const eoyBudgetValueText = getMetricSelectedValueForPeriod(
        metric,
        `${CURRENT_YEAR}EOY`,
        'budget'
      );
      const compactEoyBudgetValue = formatCompactMetricValue(eoyBudgetValueText);
      const baselineSummaryFallback = getMetricSummaryDisplay(
        compactBaselineValue.displayValue,
        t('baseline').toString(),
        t('no_baseline').toString()
      );
      const eoyBudgetSummaryFallback = getMetricSummaryDisplay(
        compactEoyBudgetValue.displayValue,
        t('eoy_budget').toString(),
        t('no_eoy_budget').toString()
      );
      const metricSummarySegments =
        typeaheadSummaryMode === 'baselineEoyBudget'
          ? [
              {id: 'owner', text: ownerName},
              {id: 'baseline', text: baselineSummaryFallback},
              {id: 'eoyBudget', text: eoyBudgetSummaryFallback},
            ]
          : [
              {id: 'owner', text: ownerName},
              {id: 'current', text: currentSummaryFallback},
              {id: 'target', text: targetSummaryFallback},
            ];
      const metricSummaryFallback = metricSummarySegments
        .map((segment) => segment.text)
        .filter(Boolean)
        .join(' • ');
      const metricSummaryTooltipParts =
        typeaheadSummaryMode === 'baselineEoyBudget'
          ? [
              compactBaselineValue.tooltipValue
                ? `${t('baseline').toString()}: ${compactBaselineValue.tooltipValue}`
                : undefined,
              compactEoyBudgetValue.tooltipValue
                ? `${t('eoy_budget').toString()}: ${compactEoyBudgetValue.tooltipValue}`
                : undefined,
            ].filter((part): part is string => Boolean(part))
          : [
              compactCurrentValue.tooltipValue
                ? `${t('current').toString()}: ${compactCurrentValue.tooltipValue}`
                : undefined,
              compactForecastValue.tooltipValue
                ? `${t('target').toString()}: ${compactForecastValue.tooltipValue}`
                : undefined,
            ].filter((part): part is string => Boolean(part));
      const typeaheadNorthStarMetricIcon = isNorthStarMetric ? (
        <NorthStarMetricIcon className={styles.redesignMetricsTypeaheadPrimaryIcon} />
      ) : null;

      return (
        <div key={getOutcomeKey(metric)} className={styles.redesignMetricsTypeaheadRow}>
          <Box className={styles.redesignMetricTitleRow}>
            {typeaheadMnpiMetricIcon}
            {typeaheadNorthStarMetricIcon}
            <Box className={styles.redesignMetricTitle}>
              {typeaheadSectionLabel ? (
                <OptionalTooltip
                  enabled={metricSummaryTooltipParts.length > 0}
                  title={
                    <Stack gap="2">
                      {metricSummaryTooltipParts.map((part) => (
                        <Text key={part} size="small">
                          {part}
                        </Text>
                      ))}
                    </Stack>
                  }
                  placement="top"
                  wrapperClassName={styles.redesignMetricsTypeaheadDetailsTooltip}
                >
                  <RedesignPlanningCommentableMetricDetails
                    metric={metric}
                    sectionLabel={typeaheadSectionLabel}
                    metricTitle={metricTitleText || metric.title}
                    summaryDisplay={metricSummaryFallback}
                    summarySegments={metricSummarySegments}
                    showSectionLabel={showTypeaheadSectionLabel}
                    metricTitleClassName={confidentialMetricTitleClassName}
                    planningCollabOutcomeComments={planningCollabOutcomeComments}
                    commentMentions={commentMentions}
                  />
                </OptionalTooltip>
              ) : (
                <RedesignCommentableTitle
                  item={metric}
                  commentMentions={commentMentions}
                  planningCollabOutcomeComments={planningCollabOutcomeComments}
                  titleWrapperClassName={styles.redesignMetricsTypeaheadTitle}
                  titleTypography={{textSize: 'medium'}}
                  titleContentClassName={cx(
                    styles.redesignMetricsTypeaheadTitleContent,
                    confidentialMetricTitleClassName
                  )}
                />
              )}
            </Box>
          </Box>
          {showTypeaheadMetricStatus ? (
            <Box display="flex" marginTop="4">
              <MetricStatusControl
                metric={metric}
                handleMetricChange={metricChangeHandler}
                statusChangeSource={statusChangeSource}
                workstreamId={workstreamId}
              />
            </Box>
          ) : null}
        </div>
      );
    }

    if (variant === 'details') {
      return (
        <div
          key={getOutcomeKey(metric)}
          className={cx(
            styles.redesignMetricsRow,
            styles.redesignMetricsRowDetails,
            showBaselineColumn && styles.redesignMetricsRowWithBaseline,
            showActualColumn && styles.redesignMetricsRowWithActual,
            styles.redesignMetricsRowDetailsData
          )}
        >
          <div
            className={cx(
              styles.redesignMetricsCell,
              styles.redesignMetricsStatusCell,
              styles.redesignMetricsStatusCellDetails
            )}
          >
            <MetricStatusControl
              metric={metric}
              handleMetricChange={metricChangeHandler}
              statusChangeSource={statusChangeSource}
              workstreamId={workstreamId}
            />
          </div>
          <div className={styles.redesignMetricsCell}>
            <div className={cx(styles.redesignMetricCellContent, styles.redesignMetricsNameCell)}>
              <WorkstreamMetricNameCellContent
                metric={metric}
                nameData={metricNameData}
                commentMentions={commentMentions}
                planningCollabOutcomeComments={planningCollabOutcomeComments}
                showPrimaryMetricIcon={variantConfig.showPrimaryMetricIcon}
                workstreamId={workstreamId}
              />
            </div>
          </div>
          {showBaselineColumn ? (
            <div
              className={cx(
                styles.redesignMetricsCell,
                styles.redesignMetricsValueBold,
                styles.redesignMetricsCurrentCell
              )}
            >
              {renderMetricValueDisplay(compactBaselineValue, styles.redesignMetricsCurrentValue)}
            </div>
          ) : null}
          <div
            className={cx(
              styles.redesignMetricsCell,
              styles.redesignMetricsValueBold,
              styles.redesignMetricsCurrentCell,
              styles.redesignMetricsCurrentColumnCell
            )}
          >
            {metricChangeHandler ? (
              <EditableMetricValue
                metric={metric}
                metricTitleText={metricTitleText}
                column="current"
                handleMetricChange={metricChangeHandler}
                isPending={isPending}
              />
            ) : (
              renderMetricValueDisplay(compactCurrentValue, styles.redesignMetricsCurrentValue)
            )}
          </div>
          <div className={cx(styles.redesignMetricsCell, styles.redesignMetricsForecastGridCell)}>
            <MetricForecastGrid
              year={detailsMetricsYear}
              labelClassName={styles.redesignMetricsForecastGridLabel}
              renderCell={({field, period}) => (
                <span className={styles.redesignMetricsForecastGridValue}>
                  {getMetricGridValueForPeriod(metric, period, field)}
                </span>
              )}
            />
          </div>
        </div>
      );
    }

    return (
      <div
        key={getOutcomeKey(metric)}
        className={cx(
          styles.redesignMetricsRow,
          showBaselineColumn && styles.redesignMetricsRowWithBaseline,
          showActualColumn && styles.redesignMetricsRowWithActual
        )}
      >
        <div className={styles.redesignMetricsCell}>
          <div className={cx(styles.redesignMetricCellContent, styles.redesignMetricsNameCell)}>
            {variantConfig.showPrimaryMetricIcon && isPrimaryMetric(metric, workstreamId) ? (
              <NorthStarMetricIcon />
            ) : null}
            <Stack gap="2" className={styles.redesignMetricContent}>
              <Box className={styles.redesignMetricTitleRow}>
                <Box className={styles.redesignMetricTitle}>
                  <span className={styles.redesignMetricTitleTooltipTrigger}>
                    {combineMetricTitleAndMetaComments ? (
                      <RedesignPlanningCommentableMetricTitleMeta
                        metric={metric}
                        metricTitle={metricTitleText || metric.title}
                        metaDisplay={metricMetaDisplay}
                        metaSegments={[
                          {id: 'signal', text: signal ? t(signal).toString() : undefined},
                          {id: 'owner', text: ownerName},
                        ]}
                        metaAccessory={metricTitleMetaAccessory}
                        metricTitleClassName={confidentialMetricTitleClassName}
                        planningCollabOutcomeComments={planningCollabOutcomeComments}
                        commentMentions={commentMentions}
                      />
                    ) : (
                      <RedesignCommentableTitle
                        item={metric}
                        commentMentions={commentMentions}
                        planningCollabOutcomeComments={planningCollabOutcomeComments}
                        titleTypography={isUpdateModalVariant ? {textSize: 'medium'} : undefined}
                        titleWrapperClassName={
                          isUpdateModalVariant
                            ? styles.redesignInlineEditableContentUpdateModal
                            : undefined
                        }
                        titleContentClassName={confidentialMetricTitleClassName}
                      />
                    )}
                  </span>
                </Box>
              </Box>
              {combineMetricTitleAndMetaComments ? null : (
                <Box className={styles.redesignMetricMetaRow}>
                  {renderRedesignMetaItems(
                    [
                      mnpiMetricMetaItem,
                      northStarMetricMetaItem,
                      ...(planningCollabOutcomeComments && (signal || ownerName)
                        ? [
                            {
                              key: 'meta',
                              kind: 'text' as const,
                              content: (
                                <RedesignPlanningCommentableMetricMetaRow
                                  metric={metric}
                                  signalLabel={signal ? t(signal).toString() : undefined}
                                  ownerDisplayName={ownerName}
                                  planningCollabOutcomeComments={planningCollabOutcomeComments}
                                  commentMentions={commentMentions}
                                />
                              ),
                            },
                          ]
                        : [
                            signal
                              ? {
                                  key: 'signal',
                                  kind: 'text' as const,
                                  content: (
                                    <RedesignPlanningCommentableMetricSignal
                                      metric={metric}
                                      signal={signal}
                                      planningCollabOutcomeComments={planningCollabOutcomeComments}
                                      commentMentions={commentMentions}
                                    />
                                  ),
                                }
                              : null,
                            ownerName
                              ? {
                                  key: 'owner',
                                  kind: 'text' as const,
                                  content: (
                                    <RedesignPlanningCommentableMetricOwner
                                      metric={metric}
                                      ownerDisplayName={ownerName}
                                      planningCollabOutcomeComments={planningCollabOutcomeComments}
                                      commentMentions={commentMentions}
                                      contentClassName={styles.redesignMetricOwnerMeta}
                                    />
                                  ),
                                }
                              : null,
                          ]),
                    ].filter(Boolean) as RedesignMetaItem[]
                  )}
                </Box>
              )}
            </Stack>
          </div>
        </div>
        {showBaselineColumn ? (
          <div
            className={cx(
              styles.redesignMetricsCell,
              styles.redesignMetricsValueBold,
              styles.redesignMetricsCurrentCell
            )}
          >
            {renderMetricValueDisplay(compactBaselineValue, styles.redesignMetricsCurrentValue)}
          </div>
        ) : null}
        {showActualColumn ? (
          <div
            className={cx(
              styles.redesignMetricsCell,
              styles.redesignMetricsCurrentCell,
              styles.redesignMetricsCurrentColumnCell
            )}
          >
            {metricChangeHandler ? (
              <EditableMetricValue
                metric={metric}
                metricTitleText={metricTitleText}
                column="actual"
                actualPeriod={actualPeriod}
                handleMetricChange={metricChangeHandler}
                isPending={isPending}
              />
            ) : (
              getMetricGridValueForPeriod(metric, actualPeriod ?? '', 'actual')
            )}
          </div>
        ) : null}
        <div
          className={cx(
            styles.redesignMetricsCell,
            styles.redesignMetricsValueBold,
            styles.redesignMetricsCurrentCell,
            styles.redesignMetricsCurrentColumnCell
          )}
        >
          {metricChangeHandler ? (
            <EditableMetricValue
              metric={metric}
              metricTitleText={metricTitleText}
              column="current"
              handleMetricChange={metricChangeHandler}
              isPending={isPending}
              showTouchCurrentUpdatedAtControl={
                isUpdateModalVariant && showTouchCurrentUpdatedAtControls
              }
              touchCurrentUpdatedAtChecked={touchCurrentUpdatedAtMetricIds?.has(metric.id)}
            />
          ) : (
            renderMetricValueDisplay(compactCurrentValue, styles.redesignMetricsCurrentValue)
          )}
        </div>
        {!hideForecastColumn ? (
          <div
            className={cx(
              styles.redesignMetricsCell,
              styles.redesignMetricsValueBold,
              styles.redesignMetricsForecastCell
            )}
          >
            {renderMetricValueDisplay(compactForecastValue, styles.redesignMetricsForecastValue)}
          </div>
        ) : null}
        <div className={cx(styles.redesignMetricsCell, styles.redesignMetricsStatusCell)}>
          <MetricStatusControl
            metric={metric}
            handleMetricChange={metricChangeHandler}
            mediumIconOnly={isUpdateModalVariant}
            statusChangeSource={statusChangeSource}
            workstreamId={workstreamId}
            iconOnly
          />
        </div>
      </div>
    );
  };

  return (
    <Stack gap="6">
      <div
        className={cx(
          styles.redesignMetricsTable,
          isUpdateModalVariant && styles.redesignMetricsTableUpdateModal,
          isUpdateModalVariant &&
            hideForecastColumn &&
            styles.redesignMetricsTableUpdateModalWithoutForecastColumn
        )}
      >
        {isTypeaheadNodeDisplay || hideHeader ? null : variant === 'details' ? (
          <div
            className={cx(
              styles.redesignMetricsRow,
              styles.redesignMetricsRowDetails,
              showBaselineColumn && styles.redesignMetricsRowWithBaseline,
              showActualColumn && styles.redesignMetricsRowWithActual
            )}
          >
            <div
              aria-hidden="true"
              className={cx(styles.redesignMetricsHeader, styles.redesignMetricsStatusHeader)}
            />
            <div
              aria-hidden="true"
              className={cx(styles.redesignMetricsHeader, styles.redesignMetricsNameHeader)}
            />
            {showBaselineColumn ? (
              <div className={styles.redesignMetricsHeader}>{t('baseline').toString()}</div>
            ) : null}
            <div className={cx(styles.redesignMetricsHeader, styles.redesignMetricsCurrentHeader)}>
              {t('current').toString()}
            </div>
            <MetricForecastHeader
              className={styles.redesignMetricsForecastGridHeader}
              spacerClassName={styles.redesignMetricsForecastGridHeaderSpacer}
            />
          </div>
        ) : (
          <div
            className={cx(
              styles.redesignMetricsRow,
              showBaselineColumn && styles.redesignMetricsRowWithBaseline,
              showActualColumn && styles.redesignMetricsRowWithActual
            )}
          >
            <div
              aria-hidden="true"
              className={cx(styles.redesignMetricsHeader, styles.redesignMetricsNameHeader)}
            />
            {showBaselineColumn ? (
              <div className={styles.redesignMetricsHeader}>{t('baseline').toString()}</div>
            ) : null}
            {showActualColumn ? (
              <div
                className={cx(styles.redesignMetricsHeader, styles.redesignMetricsCurrentHeader)}
              >
                <Box display="flex" alignItems="center">
                  <span>{formatMetricsPeriodColumnLabel(actualPeriod ?? '', 'actual')}</span>
                  <Tooltip title={t('metric_values_actual_tooltip').toString()} placement="top">
                    <span
                      className={atoms({display: 'inline-flex', marginLeft: '4'})}
                      tabIndex={0}
                      role="img"
                      aria-label={t('metric_values_actual_tooltip').toString()}
                    >
                      <UIIcon src={InfoLine} size="small" />
                    </span>
                  </Tooltip>
                </Box>
              </div>
            ) : null}
            <div className={cx(styles.redesignMetricsHeader, styles.redesignMetricsCurrentHeader)}>
              {t('current').toString()}
            </div>
            {!hideForecastColumn ? (
              <div className={styles.redesignMetricsHeader}>
                {formatMetricsPeriodColumnLabel(selectedPeriod, selectedForecastType)}
              </div>
            ) : null}
            <div className={cx(styles.redesignMetricsHeader, styles.redesignMetricsStatusHeader)}>
              {t('status').toString()}
            </div>
          </div>
        )}
        {defaultVisibleMetrics.map(renderMetricRow)}
        {showAll ? remainingVisibleMetrics.map(renderMetricRow) : null}
      </div>
      {(!disableCreateMetric && handleMetricChange && !isDetailsVariant) ||
      hiddenMetricCount > 0 ? (
        <div className={styles.redesignMetricsFooterRow}>
          {hiddenMetricCount > 0 ? (
            <Button
              type="button"
              variant="transparent"
              size="small"
              hasUnderline
              onClick={toggleShowAll}
            >
              {showAll ? t('show_less') : t('show_more_count', {count: hiddenMetricCount})}
            </Button>
          ) : null}
          {hiddenMetricCount > 0 ? (
            <span className={styles.redesignMetricsFooterDot}>•</span>
          ) : null}
          {!disableCreateMetric && handleMetricChange && !isDetailsVariant ? (
            <WorkstreamManageOutcomesTrigger
              workstreamId={workstreamId}
              workstreamName={workstreamName}
              metrics={allEditableMetrics}
              jiraOutcomes={jiraOutcomes}
              initialTab="metrics"
              isPending={isPending}
              workstreamOwner={owner as EmployeeRoleLevel | undefined}
              onSaveMetric={async (metricId, update, reason, existingMetric) => {
                return handleMetricChange?.(metricId, update, reason, existingMetric);
              }}
              onAttachMetric={onAttachMetric}
              onDeleteMetric={onDelete}
              loadElevatableJiraOutcomes={loadElevatableJiraOutcomes}
              onElevateJiraOutcome={onElevateJiraOutcome}
              onDetachJiraOutcome={onDetachJiraOutcome}
              metricPeriodOptions={metricPeriodOptions}
              hiddenMnpiMetricsCount={hiddenMnpiMetricsCount}
              triggerVariant="underline"
              triggerLabel={t('edit').toString()}
            />
          ) : null}
        </div>
      ) : null}
      <RedesignClosedItemsGroup
        count={closedMetrics.length}
        isExpanded={showClosedMetrics}
        onToggle={toggleClosedMetrics}
      >
        <div className={styles.redesignMetricsTable}>{closedMetrics.map(renderMetricRow)}</div>
      </RedesignClosedItemsGroup>
    </Stack>
  );
};

export const RedesignOutcomesList = ({
  workstreamId,
  workstreamName,
  outcomes,
  allEditableMetrics,
  owner,
  selectedFilters,
  handleMetricChange,
  onAttachMetric,
  loadElevatableJiraOutcomes,
  onElevateJiraOutcome,
  onDetachJiraOutcome,
  commentMentions = [],
  planningCollabOutcomeComments,
  onDelete,
  isPending = false,
  disableCreateMetric = false,
  showAllOutcomesByDefault = false,
  showCompletedOutcomesByDefault = false,
  onExpandRow,
  onActiveExpandedChange,
  onCompletedExpandedChange,
  variant = 'table',
  displayMode = 'default',
  outcomeTooltipPlacement,
  combineOutcomeTitleAndMetaComments = false,
  outcomeTitleLines,
}: {
  workstreamId: number;
  workstreamName?: string;
  outcomes: Array<WorkstreamMetric | JiraObject>;
  // See RedesignOutcomeItem's titleLines (/workstreams cards clamp details rows).
  outcomeTitleLines?: number;
  allEditableMetrics: WorkstreamMetric[];
  owner?: {name?: string | null};
  selectedFilters: RedesignOutcomeFilter[];
  handleMetricChange?: WorkstreamMetricChangeHandler;
  onAttachMetric?: (
    attachRequest: MetricAttachRequest,
    reason?: string
  ) => Promise<WorkstreamMetric | void>;
  commentMentions?: EmployeeMentionTypeaheadOption[];
  planningCollabOutcomeComments?: PlanningCollabOutcomeCommentsConfig;
  onDelete?: (metric: WorkstreamMetric, reason: string) => Promise<void>;
  isPending?: boolean;
  disableCreateMetric?: boolean;
  showAllOutcomesByDefault?: boolean;
  showCompletedOutcomesByDefault?: boolean;
  onExpandRow?: () => void;
  onActiveExpandedChange?: (expanded: boolean) => void;
  onCompletedExpandedChange?: (expanded: boolean) => void;
  variant?: RedesignOutcomesListVariant;
  displayMode?: RedesignOutcomesListDisplayMode;
  outcomeTooltipPlacement?: RedesignOutcomeTooltipPlacement;
  combineOutcomeTitleAndMetaComments?: boolean;
} & JiraElevationActions) => {
  const tableDensity = useAtomValue(workstreamTableDensityAtom);
  const isDetailsVariant = variant === 'details';
  const isUpdateModalVariant = variant === 'table-in-update-modal';
  const isTypeaheadNodeDisplay = displayMode === 'typeahead-node';
  const previousCompletedCountRef = useRef<number | null>(null);
  const previousOutcomeCountRef = useRef<number | null>(null);
  const onCompletedExpandedChangeRef = useRef(onCompletedExpandedChange);
  const [showAllActive, setShowAllActive] = useState(false);
  const [showCompletedOutcomes, setShowCompletedOutcomes] = useState(false);
  const [completionUrlPrompt, setCompletionUrlPrompt] = useState<CompletionUrlPromptState | null>(
    null
  );
  const filteredOutcomes = useMemo(
    () => outcomes.filter((item) => isRedesignOutcomeIncludedByFilters(item, selectedFilters)),
    [outcomes, selectedFilters]
  );
  const sortedOutcomes = useMemo(
    () => [...filteredOutcomes].sort((a, b) => sortOutcomesForWorkstream(a, b, workstreamId)),
    [filteredOutcomes, workstreamId]
  );
  const isCompletionUrlPromptOpenFor = useCallback(
    (item: WorkstreamMetric | JiraObject) =>
      getOutcomeKey(item) === completionUrlPrompt?.outcomeKey,
    [completionUrlPrompt]
  );
  const handleCompletionUrlPromptOpenChange = useCallback(
    (item: WorkstreamMetric | JiraObject, isOpen: boolean) => {
      const outcomeKey = getOutcomeKey(item);
      const preserveActiveSection = !isCompletedOutcome(item);
      if (isOpen && !preserveActiveSection) {
        setShowCompletedOutcomes(true);
        onCompletedExpandedChangeRef.current?.(true);
      }
      setCompletionUrlPrompt((current) => {
        if (isOpen) return {outcomeKey, preserveActiveSection};
        return current?.outcomeKey === outcomeKey ? null : current;
      });
    },
    []
  );
  const activeOutcomes = useMemo(
    () =>
      sortedOutcomes.filter(
        (item) =>
          !isCompletedOutcome(item) ||
          (completionUrlPrompt?.preserveActiveSection &&
            getOutcomeKey(item) === completionUrlPrompt.outcomeKey)
      ),
    [completionUrlPrompt, sortedOutcomes]
  );
  const completedOutcomes = useMemo(
    () =>
      sortedOutcomes.filter(
        (item) =>
          isCompletedOutcome(item) &&
          !(
            completionUrlPrompt?.preserveActiveSection &&
            getOutcomeKey(item) === completionUrlPrompt.outcomeKey
          )
      ),
    [completionUrlPrompt, sortedOutcomes]
  );
  const visibleActiveOutcomes =
    isDetailsVariant || showAllOutcomesByDefault || showAllActive
      ? activeOutcomes
      : activeOutcomes.slice(0, REDESIGN_ACTIVE_OUTCOMES_LIMIT);
  const hiddenActiveCount =
    isDetailsVariant || showAllOutcomesByDefault
      ? 0
      : Math.max(0, activeOutcomes.length - visibleActiveOutcomes.length);
  useEffect(() => {
    onCompletedExpandedChangeRef.current = onCompletedExpandedChange;
  }, [onCompletedExpandedChange]);

  useEffect(() => {
    if (
      completionUrlPrompt &&
      !sortedOutcomes.some((item) => getOutcomeKey(item) === completionUrlPrompt.outcomeKey)
    ) {
      setCompletionUrlPrompt(null);
    }
  }, [completionUrlPrompt, sortedOutcomes]);

  const toggleCompletedOutcomes = useCallback(() => {
    onExpandRow?.();
    const nextShowCompleted = !showCompletedOutcomes;
    onCompletedExpandedChangeRef.current?.(completedOutcomes.length > 0 && nextShowCompleted);
    setShowCompletedOutcomes(nextShowCompleted);
  }, [completedOutcomes.length, onExpandRow, showCompletedOutcomes]);

  useEffect(() => {
    if (isDetailsVariant || showAllOutcomesByDefault) {
      setShowAllActive(true);
      return;
    }

    const isComfortable = tableDensity === 'comfortable';
    setShowAllActive(isComfortable);
  }, [isDetailsVariant, showAllOutcomesByDefault, tableDensity]);

  useEffect(() => {
    if (previousCompletedCountRef.current == null) {
      previousCompletedCountRef.current = completedOutcomes.length;
      previousOutcomeCountRef.current = sortedOutcomes.length;
      setShowCompletedOutcomes(
        (showCompletedOutcomesByDefault || tableDensity === 'comfortable') &&
          completedOutcomes.length > 0
      );
      return;
    }

    if (completedOutcomes.length === 0) {
      setShowCompletedOutcomes(false);
      onCompletedExpandedChangeRef.current?.(false);
      previousCompletedCountRef.current = 0;
      previousOutcomeCountRef.current = sortedOutcomes.length;
      return;
    }

    if (
      tableDensity === 'compact' &&
      completedOutcomes.length > previousCompletedCountRef.current &&
      sortedOutcomes.length === previousOutcomeCountRef.current
    ) {
      setShowCompletedOutcomes(true);
      onCompletedExpandedChangeRef.current?.(true);
    }

    previousCompletedCountRef.current = completedOutcomes.length;
    previousOutcomeCountRef.current = sortedOutcomes.length;
  }, [
    completedOutcomes.length,
    showCompletedOutcomesByDefault,
    sortedOutcomes.length,
    tableDensity,
  ]);

  if (sortedOutcomes.length === 0) {
    if (isTypeaheadNodeDisplay) {
      return null;
    }

    return !disableCreateMetric && handleMetricChange ? (
      <RedesignZeroStateManageTrigger
        workstreamId={workstreamId}
        workstreamName={workstreamName}
        metrics={allEditableMetrics}
        initialTab="outcomes"
        isPending={isPending}
        owner={owner as EmployeeRoleLevel | undefined}
        onSaveMetric={async (metricId, update, reason, existingMetric) => {
          return handleMetricChange?.(metricId, update, reason, existingMetric);
        }}
        onAttachMetric={onAttachMetric}
        onDeleteMetric={onDelete}
        loadElevatableJiraOutcomes={loadElevatableJiraOutcomes}
        onElevateJiraOutcome={onElevateJiraOutcome}
        onDetachJiraOutcome={onDetachJiraOutcome}
      />
    ) : null;
  }

  if (isTypeaheadNodeDisplay) {
    return (
      <Stack gap="6">
        {sortedOutcomes.map((item) => (
          <div key={getOutcomeKey(item)}>
            <RedesignOutcomeItem
              workstreamId={workstreamId}
              item={item}
              owner={owner}
              variant="table"
              displayMode={displayMode}
              outcomeTooltipPlacement={outcomeTooltipPlacement}
              handleMetricChange={handleMetricChange}
              commentMentions={commentMentions}
              planningCollabOutcomeComments={planningCollabOutcomeComments}
              combineOutcomeTitleAndMetaComments={combineOutcomeTitleAndMetaComments}
              completionUrlPromptOpen={isCompletionUrlPromptOpenFor(item)}
              onCompletionUrlPromptOpenChange={(isOpen) =>
                handleCompletionUrlPromptOpenChange(item, isOpen)
              }
            />
          </div>
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap="6">
      <div
        className={cx({
          [styles.redesignOutcomeListDetails]: variant === 'details',
          [styles.redesignOutcomeListModalSpacing]: isUpdateModalVariant,
        })}
      >
        {visibleActiveOutcomes.map((item) => (
          <div
            key={getOutcomeKey(item)}
            className={cx({
              [styles.redesignOutcomeListItemDetails]: variant === 'details',
            })}
          >
            <RedesignOutcomeItem
              workstreamId={workstreamId}
              item={item}
              owner={owner}
              variant={variant}
              titleLines={outcomeTitleLines}
              displayMode={displayMode}
              outcomeTooltipPlacement={outcomeTooltipPlacement}
              handleMetricChange={handleMetricChange}
              commentMentions={commentMentions}
              planningCollabOutcomeComments={planningCollabOutcomeComments}
              combineOutcomeTitleAndMetaComments={combineOutcomeTitleAndMetaComments}
              completionUrlPromptOpen={isCompletionUrlPromptOpenFor(item)}
              onCompletionUrlPromptOpenChange={(isOpen) =>
                handleCompletionUrlPromptOpenChange(item, isOpen)
              }
            />
          </div>
        ))}
      </div>
      {!isDetailsVariant && !disableCreateMetric && handleMetricChange ? (
        <div className={styles.redesignFooterRow}>
          {hiddenActiveCount > 0 ||
          (showAllActive && activeOutcomes.length > REDESIGN_ACTIVE_OUTCOMES_LIMIT) ? (
            <>
              <Button
                type="button"
                variant="transparent"
                size="small"
                hasUnderline
                onClick={() => {
                  onExpandRow?.();
                  const nextExpanded = !showAllActive;
                  onActiveExpandedChange?.(nextExpanded);
                  setShowAllActive(nextExpanded);
                }}
              >
                {showAllActive && activeOutcomes.length > REDESIGN_ACTIVE_OUTCOMES_LIMIT
                  ? t('show_less')
                  : t('show_more_count', {count: hiddenActiveCount})}
              </Button>
              <span className={styles.redesignFooterDot}>•</span>
            </>
          ) : null}
          <WorkstreamManageOutcomesTrigger
            workstreamId={workstreamId}
            workstreamName={workstreamName}
            metrics={allEditableMetrics}
            jiraOutcomes={outcomes.filter((item): item is JiraObject => isJiraObjectLike(item))}
            initialTab="outcomes"
            isPending={isPending}
            workstreamOwner={owner as EmployeeRoleLevel | undefined}
            onSaveMetric={async (metricId, update, reason, existingMetric) => {
              return handleMetricChange?.(metricId, update, reason, existingMetric);
            }}
            onAttachMetric={onAttachMetric}
            onDeleteMetric={onDelete}
            loadElevatableJiraOutcomes={loadElevatableJiraOutcomes}
            onElevateJiraOutcome={onElevateJiraOutcome}
            onDetachJiraOutcome={onDetachJiraOutcome}
            metricPeriodOptions={[]}
            triggerVariant="underline"
            triggerLabel={t('edit').toString()}
          />
        </div>
      ) : null}
      <RedesignClosedItemsGroup
        count={completedOutcomes.length}
        isExpanded={showCompletedOutcomes}
        onToggle={toggleCompletedOutcomes}
        divided={isDetailsVariant && visibleActiveOutcomes.length > 0}
      >
        <Stack gap="6" className={styles.redesignCompletedItems}>
          <div
            className={cx({
              [styles.redesignOutcomeListDetails]: variant === 'details',
              [styles.redesignOutcomeListModalSpacing]: isUpdateModalVariant,
            })}
          >
            {completedOutcomes.map((item) => (
              <div
                key={getOutcomeKey(item)}
                className={cx({
                  [styles.redesignOutcomeListItemDetails]: variant === 'details',
                })}
              >
                <RedesignOutcomeItem
                  workstreamId={workstreamId}
                  item={item}
                  owner={owner}
                  variant={variant}
                  titleLines={outcomeTitleLines}
                  displayMode={displayMode}
                  outcomeTooltipPlacement={outcomeTooltipPlacement}
                  handleMetricChange={handleMetricChange}
                  commentMentions={commentMentions}
                  planningCollabOutcomeComments={planningCollabOutcomeComments}
                  combineOutcomeTitleAndMetaComments={combineOutcomeTitleAndMetaComments}
                  completionUrlPromptOpen={isCompletionUrlPromptOpenFor(item)}
                  onCompletionUrlPromptOpenChange={(isOpen) =>
                    handleCompletionUrlPromptOpenChange(item, isOpen)
                  }
                />
              </div>
            ))}
          </div>
        </Stack>
      </RedesignClosedItemsGroup>
    </Stack>
  );
};

export const RedesignOutcomeTypeSelector = ({
  selectedFilters,
  onToggle,
  onSelect,
}: {
  selectedFilters: RedesignOutcomeFilter[];
  onToggle?: (filter: RedesignOutcomeFilter) => void;
  onSelect?: (filter: RedesignOutcomeFilter | 'all') => void;
}) => {
  const handleSelect = (filter: RedesignOutcomeFilter) => {
    if (onToggle) {
      onToggle(filter);
      return;
    }
    onSelect?.(filter);
  };

  return (
    <Menu.Wrapper closeOnSelection={false} style={{marginRight: -8, marginLeft: 'auto'}}>
      {({getContentProps, getTriggerProps}) => (
        <>
          <Button
            variant="borderless"
            size="small"
            withDropdownIcon
            className={styles.redesignHeaderSelectorButton}
            {...getTriggerProps()}
          >
            {t('type').toString()}
          </Button>
          <Menu.Content {...getContentProps()} placement="bottom-end" style={{minWidth: 180}}>
            <Menu.Segment withLabel={t('type')}>
              {REDESIGN_OUTCOME_TYPE_OPTIONS.map((option) => {
                const isSelected = selectedFilters.includes(option.value);
                return (
                  <Menu.ActionItem
                    key={option.value}
                    onClick={() => handleSelect(option.value)}
                    withLeftAccessory={<Checkbox checked={isSelected} readOnly tabIndex={-1} />}
                  >
                    <Box display="flex" alignItems="center" style={{gap: 8}}>
                      <UIIcon src={option.icon} size="small" />
                      <span>{t(option.labelKey).toString()}</span>
                    </Box>
                  </Menu.ActionItem>
                );
              })}
            </Menu.Segment>
          </Menu.Content>
        </>
      )}
    </Menu.Wrapper>
  );
};
